import React, { useEffect, useState, useCallback, useMemo, useRef } from 'react';
import CollapsibleTable from '../../components/table';
import axios from 'axios';
import config from '../../config/config';
import { useSession } from '../../components/hooks/useSession';
import SimpleSuccessSpinner from '../../components/LoadingSpinner';
import handleOpenSweetAlert from '../../components/handleOpenSweetAlert';
import ExportButton from '../../components/BotonEx';
import { buildFilterPayload } from '../../utils/filterUtils';
import { TableFilterChips } from '../../components/Table/components/TableFilterChips';


import ClearIcon from '@mui/icons-material/Clear';
import SearchIcon from '@mui/icons-material/Search';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import AccountTreeIcon from '@mui/icons-material/AccountTree';
import FilterListIcon from '@mui/icons-material/FilterList';
import CheckBoxOutlineBlankIcon from '@mui/icons-material/CheckBoxOutlineBlank';
import CheckBoxIcon from '@mui/icons-material/CheckBox';
import {
  Autocomplete,
  Badge,
  Box,
  Paper,
  TextField,
  Button,
  Checkbox,
  IconButton,
  InputAdornment,
  Popover,
  Dialog,
  DialogTitle,
  DialogContent,
  Typography,
  Stack,
  Chip,
  CircularProgress,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  Tooltip,
  Collapse,
} from '@mui/material';
import CancelacionModal from '../../components/CancelacionModal';
import FileUploadModal from '../../components/FileUploadModal';

import VisibilityIcon from '@mui/icons-material/Visibility';
import DownloadIcon from '@mui/icons-material/Download';

/* ===================== MAPAS BASE TABLA + EXCEL ===================== */
const exportLabels = {
  ID_SOLICITUD:         'ID SOLICITUD',
  REQUI:                'REQUI',
  ORDEN:                'ORDEN',
  EMPRESA:              'EMPRESA',
  FECHA_OC:             'FECHA OC',
  FECHA_SOLICITUD:      'FECHA SOLICITUD',
  REQUISITOR:           'REQUISITOR',
  COMPRADOR:            'COMPRADOR',
  PROVEEDOR:            'PROVEEDOR',
  NUMERO_CC:            'NUMERO CC',
  NOMBRE_CC:            'NOMBRE CC',
  DEPARTAMENTO:         'DEPARTAMENTO',
  TOTAL_OC:             'TOTAL OC',
  DIVISA:               'DIVISA',
  DESCRIPCION:          'DESCRIPCION',
  MONTOSOLICITADO:      'MONTO SOLICITADO',
  PROGRAMAR_A_PAGO:     'PROGRAMAR A PAGO',
  PENDIENTE:            'PENDIENTE',
  ECONOMICO:            'ECONOMICO',
  NOTAS_REQUI:          'NOTAS REQUI',
  OBSERVACIONES:        'OBSERVACIONES SOLICITUD',
  ESTATUS:              'ESTATUS',
  SOLICITUD:            'SOLICITUD',
  SOLICITAR_PAGO:       'SOLICITAR PAGO',
  FECHA_SOLICITAR_PAGO: 'FECHA SOLICITAR PAGO',
  PAGADO:               'PAGADO ERP',
  FECHA_PAGO:           'FECHA PAGO ERP',
  DIAS_SIN_PAGO:        'DIAS SIN PAGO',
  MESES_SIN_PAGO:       'MESES SIN PAGO',
  ENTRADA:              'ENTRADA',
  FECHA_ENTRADA:        'FECHA ENTRADA',
  COMPROBANTE:          'COMPROBANTE',
  FECHA_COMPROBANTE:    'FECHA COMPROBANTE',
  FACTURAS_ASOCIADAS:   'FACTURAS ASOCIADAS',
  EXHIBIT:              'EXHIBIT',
};



/* ===================== HELPERS ===================== */

// ✅ Normaliza idEmpresa que puede venir como:
//  - [2]
//  - "2"
//  - "[1,2,3]"
//  - "1,2,3"
const normalizeEmpresaArray = (idEmpresa) => {
  if (idEmpresa == null) return [];

  if (Array.isArray(idEmpresa)) {
    return idEmpresa
      .map((x) => Number(x))
      .filter((n) => Number.isFinite(n) && n > 0);
  }

  if (typeof idEmpresa === 'string') {
    const s = idEmpresa.trim();

    if (s.startsWith('[') && s.endsWith(']')) {
      try {
        const arr = JSON.parse(s);
        if (Array.isArray(arr)) {
          return arr
            .map((x) => Number(x))
            .filter((n) => Number.isFinite(n) && n > 0);
        }
      } catch {}
    }

    if (s.includes(',')) {
      return s
        .split(',')
        .map((x) => Number(x.trim()))
        .filter((n) => Number.isFinite(n) && n > 0);
    }

    const n = Number(s);
    return Number.isFinite(n) && n > 0 ? [n] : [];
  }

  const n = Number(idEmpresa);
  return Number.isFinite(n) && n > 0 ? [n] : [];
};

const buildEmpresaScope = (idEmpresa) => {
  const arr = normalizeEmpresaArray(idEmpresa)
    .filter((n) => Number.isFinite(n) && n > 0)
    .sort((a, b) => a - b);

  return arr.length ? arr.join(',') : '0';
};

const formatInputDate = (date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const getCurrentMonthRange = () => {
  const today = new Date();
  return {
    start: formatInputDate(new Date(today.getFullYear(), today.getMonth(), 1)),
    end: formatInputDate(new Date(today.getFullYear(), today.getMonth() + 1, 0)),
  };
};



const getUniqueOptions = (values) => (
  Array.from(new Set(values.map(value => String(value ?? '').trim()).filter(Boolean)))
    .sort((a, b) => a.localeCompare(b, 'es'))
);

const parseDateForSort = (value) => {
  if (!value) return 0;
  if (value instanceof Date) return value.getTime();

  const text = String(value).trim();
  const ddmmyyyyWithTime = text.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})(?:\s+(\d{1,2}):(\d{1,2})(?::(\d{1,2}))?)?/);
  if (ddmmyyyyWithTime) {
    const [, day, month, year, hours = 0, minutes = 0, seconds = 0] = ddmmyyyyWithTime;
    return new Date(Number(year), Number(month) - 1, Number(day), Number(hours), Number(minutes), Number(seconds)).getTime();
  }

  const parsed = new Date(text).getTime();
  return Number.isFinite(parsed) ? parsed : 0;
};

const extractFechaPagoFromRow = (row = {}) => {
  const candidates = [
    row.PAGO_ERP_FECHA,
    row.PAGO_OPERATIVO_FECHA,
  ];

  for (const c of candidates) {
    if (c && String(c).trim()) return String(c).trim();
  }

  return '';
};

const sortByRecentDate = (rows = [], filterMode = 'FECHA_OC') => (
  [...rows].sort((a, b) => {
    if (filterMode === 'FECHA_PAGO') {
      const fechaB = extractFechaPagoFromRow(b);
      const fechaA = extractFechaPagoFromRow(a);
      const dateB = parseDateForSort(fechaB || b.FECHA_OC);
      const dateA = parseDateForSort(fechaA || a.FECHA_OC);
      return dateB - dateA;
    }
    return (
      parseDateForSort(b.FECHA_OC || b.FECHASOLICITUD || b.FECHA_SOLICITUD) -
      parseDateForSort(a.FECHA_OC || a.FECHASOLICITUD || a.FECHA_SOLICITUD)
    );
  })
);

const buildCentroCostoOption = (row = {}) => {
  const numero = String(row.NUMEROCC || row.NUMERO_CENTRO_COSTO || row.NUMERO_CC || '').trim();
  const nombre = String(row.CENTROCOSTO || row.CENTRO_COSTO || row.NOMBRE_CC || '').trim();

  if (numero && nombre && numero.toUpperCase() !== nombre.toUpperCase()) return `${numero} - ${nombre}`;
  return numero || nombre;
};

const splitCentroCostoOption = (value = '') => (
  String(value)
    .split(' - ')
    .map(part => part.trim())
    .filter(Boolean)
);

const getCentroCostoFilterValue = (value = '') => {
  const parts = splitCentroCostoOption(value);
  return parts.length > 1 ? parts[parts.length - 1] : String(value).trim();
};

const getCentroCostoFilterValues = (values = []) => (
  values
    .map(getCentroCostoFilterValue)
    .filter(Boolean)
);

const getSelectedSummary = (values = []) => {
  if (!values.length) return '';
  if (values.length === 1) {
    const first = values[0];
    return typeof first === 'string' ? first : first?.nombre || String(first);
  }
  return `${values.length} seleccionadas`;
};

const renderSummaryInput = (params, label, selectedValues = []) => {
  const summary = getSelectedSummary(selectedValues);
  const isSelected = Boolean(summary);

  return (
    <TextField
      {...params}
      placeholder={summary ? undefined : label}
      InputProps={{
        ...params.InputProps,
        startAdornment: summary ? (
          <Box
            component="span"
            sx={{
              maxWidth: 'calc(100% - 24px)',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              fontSize: '0.68rem',
              fontWeight: 700,
              color: '#0655A5',
            }}
          >
            {summary}
          </Box>
        ) : params.InputProps.startAdornment,
      }}
      sx={{
        ...(isSelected ? {
          '& .MuiInputBase-root': {
            bgcolor: '#f0f7ff !important',
            borderColor: '#93c5fd !important',
            '& fieldset': {
              borderColor: '#60a5fa !important',
              borderWidth: '1.5px !important',
            }
          }
        } : {})
      }}
    />
  );
};

const icon = <CheckBoxOutlineBlankIcon fontSize="small" sx={{ fontSize: 16, color: '#94a3b8' }} />;
const checkedIcon = <CheckBoxIcon fontSize="small" sx={{ fontSize: 16, color: '#0655A5' }} />;

const renderCheckboxOption = (props, option, { selected }) => (
  <li
    {...props}
    style={{
      padding: '4px 8px',
      fontSize: '0.74rem',
      fontWeight: selected ? 700 : 500,
      backgroundColor: selected ? '#e0f2fe' : 'transparent',
      color: selected ? '#0369a1' : '#1e293b',
      display: 'flex',
      alignItems: 'center',
      gap: 6,
      borderBottom: '1px solid #f8fafc',
    }}
  >
    <Checkbox
      icon={icon}
      checkedIcon={checkedIcon}
      style={{ marginRight: 4, padding: 0 }}
      checked={selected}
      size="small"
    />
    <Box component="span" sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', flex: 1 }}>
      {typeof option === 'string' ? option : option?.nombre || ''}
    </Box>
  </li>
);

const compactAutocompleteSx = {
  '& .MuiInputBase-root': {
    height: 28,
    minHeight: 28,
    maxHeight: 28,
    borderRadius: '10px !important',
    bgcolor: '#ffffff',
    fontSize: '0.68rem',
    fontWeight: 500,
    px: '8px !important',
    py: '0px !important',
    flexWrap: 'nowrap',
    overflow: 'hidden',
    display: 'flex',
    alignItems: 'center',
    '& fieldset': { 
      borderColor: '#cbd5e1 !important',
      top: 0,
      '& legend': { display: 'none' }
    },
    '&:hover fieldset': { borderColor: '#94a3b8 !important' },
    '&.Mui-focused fieldset': { borderColor: '#0655A5 !important', borderWidth: '1.5px !important' },
    '& .MuiAutocomplete-input': {
      minWidth: '20px !important',
      py: '0px !important',
      fontSize: '0.68rem',
      color: '#0f172a',
      '&::placeholder': {
        color: '#64748b',
        opacity: 1,
        fontWeight: 500,
      }
    },
    '& .MuiAutocomplete-endAdornment': {
      position: 'absolute',
      right: '6px',
      top: '50%',
      transform: 'translateY(-50%)',
      display: 'flex',
      alignItems: 'center',
      gap: 0,
      '& .MuiIconButton-root': {
        p: '1px',
        color: '#94a3b8',
        '&:hover': { color: '#0f172a' }
      },
      '& .MuiSvgIcon-root': {
        fontSize: '15px',
      }
    }
  },
  '& .MuiFormLabel-root': {
    display: 'none',
  }
};



const normalizeOptionText = (value = '') => (
  String(value ?? '')
    .trim()
    .toUpperCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
);

const matchesAnyOption = (value, selected = []) => {
  if (!selected.length) return true;
  const normalizedValue = normalizeOptionText(value);
  return selected.some(item => normalizeOptionText(item) === normalizedValue);
};



const buildVisibleServerFilters = (filterData, options = {}) => {
  const { ignoreDateFilters = false } = options;
  const payload = { ...filterData };
  delete payload.EMPRESA_IDS;
  delete payload.DEPARTAMENTOS;
  delete payload.CENTRO_COSTOS;
  delete payload.PROVEEDORES;
  delete payload.DIVISAS;

  if (ignoreDateFilters) {
    payload.FECHA_INICIO = null;
    payload.FECHA_FIN = null;
  }

  payload.DEPARTAMENTO = filterData.DEPARTAMENTOS || [];
  payload.CENTRO_COSTO = getCentroCostoFilterValues(filterData.CENTRO_COSTOS || []);
  payload.PROVEEDOR = filterData.PROVEEDORES?.length ? filterData.PROVEEDORES : (payload.PROVEEDOR || '');
  payload.DIVISA = filterData.DIVISAS || [];
  payload.EMPRESA = '';

  return payload;
};

/* ===================== EXPORT: FECHAS AUTORIZACIÓN ===================== */
const pad2 = (n) => String(n).padStart(2, '0');

const formatFechaExcel = (fecha) => {
  if (!fecha) return '';
  const d = new Date(fecha);
  if (isNaN(d.getTime())) return '';
  return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())} ${pad2(d.getHours())}:${pad2(d.getMinutes())}`;
};

const pickMaxDateIso = (fechas) => {
  const dates = (fechas || [])
    .map((f) => new Date(f))
    .filter((d) => !isNaN(d.getTime()))
    .sort((a, b) => b - a);

  return dates.length ? dates[0].toISOString() : '';
};

const buildFechasAutExport = (row) => {
  const cadena = Array.isArray(row?.cadenaAut) ? row.cadenaAut : [];

  const aut = cadena.filter((x) => String(x?.ROL || '').startsWith('Autorizador '));
  const teso = cadena.filter((x) => String(x?.ROL || '').startsWith('Tesorero '));

  const autOk = aut.filter((x) => x?.ESTADO === 'CHECK_GREEN' && x?.FECHA_AUTORIZACION);
  const FECHA_AUT_ULTIMA = formatFechaExcel(
    pickMaxDateIso(autOk.map((x) => x.FECHA_AUTORIZACION))
  );

  const fields = { FECHA_AUT_ULTIMA };

  for (const item of aut) {
    const rol = String(item?.ROL || '');
    const match = rol.match(/Autorizador\s+(\d+)/i);
    if (!match) continue;

    const nivel = Number(match[1]);
    const key = `FECHA_AUT_N${nivel}`;

    if (item?.ESTADO === 'CHECK_GREEN') {
      fields[key] = formatFechaExcel(item?.FECHA_AUTORIZACION);
    } else if (!(key in fields)) {
      fields[key] = '';
    }
  }

  for (const item of teso) {
    const rol = String(item?.ROL || '');
    const match = rol.match(/Tesorero\s+(\d+)/i);
    if (!match) continue;

    const nivel = Number(match[1]);
    const key = `FECHA_TESO_N${nivel}`;

    if (item?.ESTADO === 'CHECK_GREEN') {
      fields[key] = formatFechaExcel(item?.FECHA_AUTORIZACION);
    } else if (!(key in fields)) {
      fields[key] = '';
    }
  }

  return fields;
};

const hasAdminExportValue = (value) => {
  if (typeof value === 'boolean') return value;
  if (typeof value === 'number') return value > 0;
  const text = String(value ?? '').trim().toUpperCase();
  return text === 'SI' || text === 'SÍ' || text === 'TRUE' || text === '1';
};

const hasPositiveCount = (value) => Number(value || 0) > 0;

const pickFirstExportDate = (...values) => (
  values.find((value) => String(value ?? '').trim()) || ''
);

const buildAdminTrackingExport = (row) => {
  const tieneSolicitud = hasPositiveCount(row?.SOLICITUDES_COUNT)
    || hasAdminExportValue(row?.TIENE_SOLICITUD)
    || hasPositiveCount(row?.ID_SOLICITUD);
  const tieneSolicitudPago = hasPositiveCount(row?.REPORTE_SOLICITADO_COUNT);
  const tienePago = hasPositiveCount(row?.PAGO_OPERATIVO_COUNT)
    || hasAdminExportValue(row?.PAGO_OPERATIVO_COMPLETO)
    || hasPositiveCount(row?.PAGO_ERP_COUNT)
    || hasAdminExportValue(row?.PAGO_ERP_COMPLETO)
    || hasPositiveCount(row?.COMPROBANTE_COUNT)
    || hasAdminExportValue(row?.TIENE_COMPROBANTE)
    || hasPositiveCount(row?.PAGADAS_COUNT)
    || hasAdminExportValue(row?.TIENE_PAGO);
  const tieneEntrada = hasPositiveCount(row?.ENTRADAS_COUNT);
  const tieneComprobante = hasPositiveCount(row?.COMPROBANTE_COUNT)
    || hasAdminExportValue(row?.TIENE_COMPROBANTE);

  return {
    EXPORT_SOLICITUD: tieneSolicitud ? 'SI' : 'NO',
    EXPORT_SOLICITUD_FECHA: tieneSolicitud
      ? pickFirstExportDate(row?.ULTIMA_SOLICITUD, row?.FECHA_SOLICITUD_PAGO, row?.FECHASOLICITUD)
      : '',
    EXPORT_SOLICITAR_PAGO: tieneSolicitudPago ? 'SI' : 'NO',
    EXPORT_SOLICITAR_PAGO_FECHA: tieneSolicitudPago ? pickFirstExportDate(row?.REPORTE_SOLICITADO_FECHA) : '',
    EXPORT_PAGADO: tienePago ? 'SI' : 'NO',
    EXPORT_PAGADO_FECHA: tienePago ? pickFirstExportDate(row?.PAGO_OPERATIVO_FECHA, row?.PAGO_ERP_FECHA, row?.ULTIMO_PAGO, row?.FECHA_PAGO, row?.COMPROBANTE_FECHA) : '',
    EXPORT_ENTRADA: tieneEntrada ? 'SI' : 'NO',
    EXPORT_ENTRADA_FECHA: tieneEntrada ? pickFirstExportDate(row?.ENTRADA_FECHA) : '',
    EXPORT_COMPROBANTE: tieneComprobante ? 'SI' : 'NO',
    EXPORT_COMPROBANTE_FECHA: tieneComprobante
      ? pickFirstExportDate(row?.COMPROBANTE_FECHA, row?.FECHA_COMPROBANTE)
      : '',
  };
};

// Calcula días transcurridos desde una fecha (formato dd/mm/yyyy o ISO) hasta hoy
const calcDiasSinPago = (fechaStr) => {
  if (!fechaStr) return null;
  const text = String(fechaStr).trim();
  let date;
  const slashMatch = text.match(/^(\d{2})\/(\d{2})\/(\d{4})/);
  if (slashMatch) {
    date = new Date(Number(slashMatch[3]), Number(slashMatch[2]) - 1, Number(slashMatch[1]));
  } else {
    date = new Date(text.split(/[T\s]/)[0]);
  }
  if (!date || isNaN(date.getTime())) return null;
  date.setHours(0, 0, 0, 0);
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return Math.max(0, Math.floor((today.getTime() - date.getTime()) / 86400000));
};

const buildAdminExportRow = (row) => {
  const tracking = buildAdminTrackingExport(row);
  const tieneSolicitud   = tracking.EXPORT_SOLICITUD     === 'SI';
  const tienePago        = tracking.EXPORT_PAGADO        === 'SI';

  // Días sin pago: desde la fecha de solicitud hasta hoy (solo si tiene solicitud y no está pagado)
  const fechaParaDias = tracking.EXPORT_SOLICITUD_FECHA || row?.ULTIMA_SOLICITUD || row?.FECHA_SOLICITUD_PAGO || '';
  const dias = (tieneSolicitud && !tienePago) ? (calcDiasSinPago(fechaParaDias) ?? 0) : 0;
  const meses = dias > 0 ? Math.round((dias / 30) * 10) / 10 : 0;

  return {
    EXHIBIT:                     row.EXHIBIT          || row.EXHIBICION || '',
    REQUI:                       row.REQUI            || '',
    FOLIO:                       row.FOLIO            || '',
    EMPRESA:                     row.EMPRESA          || '',
    FECHA_OC:                    row.FECHASOLICITUD   || row.FECHA_OC || '',
    REQUISITOR:                  row.REQUISITOR       || '',
    SOLICITANTE:                 row.SOLICITANTE      || '',
    PROVEEDOR:                   row.PROVEEDOR        || '',
    NUMEROCC:                    row.NUMEROCC         || '',
    CENTROCOSTO:                 row.CENTROCOSTO      || '',
    DEPARTAMENTO:                row.DEPARTAMENTO     || '',
    TOTAL:                       row.TOTAL            || '',
    DIVISA:                      row.DIVISA           || '',
    DESCRIPCION:                 Array.isArray(row.partidas) && row.partidas.length > 0
      ? row.partidas
          .map(p => `- ${p.DESCRIPCION_PART || 'S/D'}${p.CANTIDAD ? ` (Ct: ${p.CANTIDAD})` : ''}`)
          .join('\n')
      : (row.DESCRIPCION || ''),
    MONTOSOLICITADO:             row.MONTOSOLICITADO  || '',
    PENDIENTE:                   row.PENDIENTE        || '',
    NUMERO_ECONOMICO:            row.NUMERO_ECONOMICO || '',
    NOTAS_REQUI:                 row.NOTAS_REQUI      || '',
    ESTATUS:                     row.ESTATUS          || '',
    EXPORT_SOLICITUD:            tracking.EXPORT_SOLICITUD,
    EXPORT_SOLICITUD_FECHA:      tracking.EXPORT_SOLICITUD_FECHA,
    EXPORT_SOLICITAR_PAGO:       tracking.EXPORT_SOLICITAR_PAGO,
    EXPORT_SOLICITAR_PAGO_FECHA: tracking.EXPORT_SOLICITAR_PAGO_FECHA,
    EXPORT_PAGADO:               tracking.EXPORT_PAGADO,
    EXPORT_PAGADO_FECHA:         tracking.EXPORT_PAGADO_FECHA,
    EXPORT_DIAS_SIN_PAGO:        (tieneSolicitud && !tienePago && dias > 0) ? dias : '',
    EXPORT_MESES_SIN_PAGO:       (tieneSolicitud && !tienePago && meses > 0) ? meses : '',
    EXPORT_ENTRADA:              tracking.EXPORT_ENTRADA,
    EXPORT_ENTRADA_FECHA:        tracking.EXPORT_ENTRADA_FECHA,
    EXPORT_COMPROBANTE:          tracking.EXPORT_COMPROBANTE,
    EXPORT_COMPROBANTE_FECHA:    tracking.EXPORT_COMPROBANTE_FECHA,
    FACTURAS_ASOCIADAS:          row.FACTURAS_ASOCIADAS || row.FOLIO_FACTURA || '',
    ...buildFechasAutExport(row),
  };
};

const MAX_ADMIN_AUTH_LEVELS = 5;

const formatAdminAuthDate = (value) => {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  const day = String(date.getDate()).padStart(2, '0');
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const year = String(date.getFullYear()).slice(-2);
  const hour = String(date.getHours()).padStart(2, '0');
  const minute = String(date.getMinutes()).padStart(2, '0');
  return `${day}/${month}/${year} ${hour}:${minute}`;
};

const buildAdminAuthColumns = (row) => {
  const cadena = Array.isArray(row?.cadenaAut) ? row.cadenaAut : [];
  const autorizadores = cadena.filter((item) => String(item?.ROL || '').startsWith('Autorizador '));
  const columns = {};

  for (let level = 1; level <= MAX_ADMIN_AUTH_LEVELS; level++) {
    const item = autorizadores.find((auth) => {
      const match = String(auth?.ROL || '').match(/Autorizador\s+(\d+)/i);
      return match && Number(match[1]) === level;
    });

    if (!item) {
      columns[`AUTNIV${level}`] = 'N/A';
      columns[`AUTNIV${level}_NOMBRE`] = '';
      columns[`AUTNIV${level}_FECHA`] = '';
    } else if (item.ESTADO === 'CHECK_GREEN') {
      columns[`AUTNIV${level}`] = 1;
      columns[`AUTNIV${level}_NOMBRE`] = item.USUARIO || '';
      columns[`AUTNIV${level}_FECHA`] = formatAdminAuthDate(item.FECHA_AUTORIZACION);
    } else if (item.ESTADO === 'CANCEL_RED') {
      columns[`AUTNIV${level}`] = 'CANCEL_RED';
      columns[`AUTNIV${level}_NOMBRE`] = item.USUARIO || '';
      columns[`AUTNIV${level}_FECHA`] = formatAdminAuthDate(item.FECHA_AUTORIZACION);
    } else {
      columns[`AUTNIV${level}`] = 0;
      columns[`AUTNIV${level}_NOMBRE`] = item.USUARIO || '';
      columns[`AUTNIV${level}_FECHA`] = '';
    }
  }

  return columns;
};

const formatAdminMoney = (value, currency = 'MXN') => {
  const num = Number(value || 0);
  return num.toLocaleString('en-US', { style: 'currency', currency: currency || 'MXN' });
};

const parseAdminDate = (value) => {
  if (!value) return null;
  const text = String(value).trim().split(/[T,\s]/)[0];
  const match = text.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
  if (match) {
    const [, day, month, year] = match;
    return new Date(Number(year), Number(month) - 1, Number(day));
  }
  const parsed = new Date(text);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
};



const statusDetailTitles = {
  solicitud: 'Detalle de solicitud',
  reporte: 'Solicitudes de pago',
  entrada: 'Entradas',
  comprobante: 'Comprobante',
  pago: 'Pagado',
};

const DetailMetric = ({ label, value, tone = 'default' }) => {
  const color = tone === 'ok' ? '#059669' : tone === 'warn' ? '#d97706' : '#0f172a';
  return (
    <Box sx={{ py: 0.75, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2, borderBottom: '1px solid #eef2f7' }}>
      <Typography sx={{ fontSize: 12, color: '#64748b', fontWeight: 700 }}>{label}</Typography>
      <Typography sx={{ fontSize: 13, color, fontWeight: 900, textAlign: 'right' }}>{value}</Typography>
    </Box>
  );
};

const FlowSummaryCard = ({ title, status, detail, tone = 'default', actionLabel, onAction }) => {
  const toneMap = {
    ok: { bg: '#f0fdf4', border: '#bbf7d0', color: '#166534' },
    warn: { bg: '#fff7ed', border: '#fed7aa', color: '#9a3412' },
    bad: { bg: '#fef2f2', border: '#fecaca', color: '#991b1b' },
    default: { bg: '#f8fafc', border: '#dbe4ef', color: '#475569' },
  };
  const palette = toneMap[tone] || toneMap.default;

  return (
    <Box sx={{ p: 1.15, border: `1px solid ${palette.border}`, borderRadius: 1.2, bgcolor: palette.bg, minHeight: 86 }}>
      <Stack direction="row" alignItems="flex-start" justifyContent="space-between" gap={1}>
        <Box sx={{ minWidth: 0 }}>
          <Typography sx={{ fontSize: 10.5, color: '#64748b', fontWeight: 900, textTransform: 'uppercase' }}>{title}</Typography>
          <Typography sx={{ mt: 0.35, fontSize: 13, color: palette.color, fontWeight: 950, lineHeight: 1.15 }}>{status}</Typography>
        </Box>
        {actionLabel && (
          <Button size="small" variant="outlined" onClick={onAction} sx={{ minWidth: 0, px: 1, py: 0.25, fontSize: 10.5, fontWeight: 900, borderRadius: 5 }}>
            {actionLabel}
          </Button>
        )}
      </Stack>
      {detail && (
        <Typography sx={{ mt: 0.55, fontSize: 11, color: '#475569', fontWeight: 700, lineHeight: 1.25 }}>
          {detail}
        </Typography>
      )}
    </Box>
  );
};

const daysSinceAdminDate = (value) => {
  const date = parseAdminDate(value);
  if (!date) return null;
  date.setHours(0, 0, 0, 0);
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return Math.max(0, Math.floor((today.getTime() - date.getTime()) / 86400000));
};

const formatDateOnlyLabel = (value) => {
  if (!value) return '';
  const text = String(value).trim();
  const slashDate = text.match(/^(\d{2}\/\d{2}\/\d{4})/);
  if (slashDate) return slashDate[1];
  const isoDate = text.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (isoDate) return `${isoDate[3]}/${isoDate[2]}/${isoDate[1]}`;
  return text.split(/\s+/)[0] || text;
};

const StatusDetailDialog = ({
  open,
  onClose,
  type,
  row,
  loading,
  data,
  apiUrl,
  solicitudesRelacionadas,
  loadingSolicitudesRelacionadas,
  onSelectSolicitud,
  onOpenDetail,
}) => {
  const title = statusDetailTitles[type] || 'Detalle';
  const solicitudDetailData = type === 'solicitud' ? data?.solicitud : null;
  const authSource = Array.isArray(solicitudDetailData?.cadenaAut) ? solicitudDetailData.cadenaAut : row?.cadenaAut;
  const authFlow = Array.isArray(authSource)
    ? authSource.filter(item => String(item?.ROL || '').startsWith('Autorizador '))
    : [];
  const authComplete = authFlow.length > 0 && authFlow.every(item => item?.ESTADO === 'CHECK_GREEN');
  const nextPending = authFlow.find(item => item?.ESTADO !== 'CHECK_GREEN');


  const renderSolicitud = () => {
    const solicitudActual = solicitudDetailData?.solicitud || null;
    const related = data?.relacionadas || solicitudesRelacionadas || { solicitudes: [], total: 0, dentroRango: 0, fueraRango: 0 };
    const relatedRows = Array.isArray(related?.solicitudes) ? related.solicitudes : [];
    const selectedSolicitudId = Number(data?.selectedSolicitudId || solicitudActual?.id || row?.ID_SOLICITUD || 0);
    const reporteData = data?.reporte || {};
    const pagoData = data?.pago || {};
    const entradaData = data?.entrada || {};
    const comprobanteData = data?.comprobante || {};
    const reportes = Array.isArray(reporteData?.reportes) ? reporteData.reportes : [];
    const pagosErp = Array.isArray(pagoData?.pagosErp) ? pagoData.pagosErp : [];
    const facturas = Array.isArray(pagoData?.facturas) ? pagoData.facturas : [];
    const facturasPagadas = facturas.filter(f => Number(f.SALDO || 0) <= 0.01 && Number(f.ABONOS || 0) > 0);
    const comprobantes = Array.isArray(comprobanteData?.comprobantes) ? comprobanteData.comprobantes : [];
    const entradaPartidas = Array.isArray(entradaData?.partidas) ? entradaData.partidas : [];
    const entradas = Array.isArray(entradaData?.entradas) ? entradaData.entradas : [];
    const entradaPendientes = entradaPartidas.filter(item => Number(item.PENDIENTE || 0) > 0).length;
    const entradaCompleta = entradaPartidas.length > 0 && entradaPendientes === 0;
    const entradaParcial = !entradaCompleta && (entradas.length > 0 || entradaPartidas.some(item => Number(item.RECIBIDO || 0) > 0));
    const pagoResumen = pagoData?.resumen || {};
    const pagoOperativoCompleto = row?.PAGO_OPERATIVO_COMPLETO === true || String(row?.PAGO_OPERATIVO_COMPLETO).toUpperCase() === 'TRUE';
    const pagoCompleto = (Number(pagoResumen.saldoErp || 0) <= 0.01 && Number(pagoResumen.abonosErp || 0) > 0)
      || pagoOperativoCompleto
      || Boolean(row?.PAGO_ERP_COMPLETO);
    const montoSolicitudActual = solicitudActual?.montoSolicitado ?? solicitudActual?.monto ?? row?.MONTO_SOLICITADO ?? row?.PROGRAMAR_PAGO;
    const fechaSolicitud = solicitudActual?.fechaSolicitud || row?.ULTIMA_SOLICITUD || row?.FECHA_SOLICITUD_PAGO || '';
    const fechaSolicitudVisible = formatDateOnlyLabel(fechaSolicitud);
    const diasSinPago = !pagoCompleto ? daysSinceAdminDate(fechaSolicitud) : null;
    const pagoFecha = row?.PAGO_OPERATIVO_FECHA || row?.PAGO_ERP_FECHA || row?.ULTIMO_PAGO || row?.FECHA_PAGO || pagosErp?.[0]?.FECHA_PAGO || '';
    const firstCompWithDate = comprobantes.find((f) => f.fecha_alta || f.FECHA_ALTA);
    const comprobanteFecha = row?.COMPROBANTE_FECHA || firstCompWithDate?.fecha_alta || firstCompWithDate?.FECHA_ALTA || '';
    const reporteCount = reportes.length || Number(row?.REPORTE_SOLICITADO_COUNT || 0);
    const renderEmptyText = (text) => (
      <Typography sx={{ p: 1, fontSize: 11.5, color: '#64748b', fontWeight: 750 }}>
        {text}
      </Typography>
    );
    const renderTinyChip = (label, tone = 'default') => {
      const chipPalette = tone === 'ok'
        ? { bg: '#dcfce7', color: '#166534' }
        : tone === 'warn'
          ? { bg: '#fef3c7', color: '#92400e' }
          : { bg: '#e5e7eb', color: '#475569' };
      return (
        <Chip
          size="small"
          label={label}
          sx={{
            height: 18,
            fontSize: 9.5,
            fontWeight: 900,
            bgcolor: chipPalette.bg,
            color: chipPalette.color,
            '& .MuiChip-label': { px: 0.7 },
          }}
        />
      );
    };
    const renderFlowSection = (sectionTitle, tone, children, actionLabel, onAction) => {
      const sectionPalette = tone === 'ok'
        ? { border: '#bbf7d0', bg: '#f8fffb', title: '#166534' }
        : tone === 'warn'
          ? { border: '#fed7aa', bg: '#fffaf3', title: '#9a3412' }
          : { border: '#dbe4ef', bg: '#fff', title: '#0f172a' };
      return (
        <Box sx={{ border: `1px solid ${sectionPalette.border}`, borderRadius: 1.2, bgcolor: sectionPalette.bg, overflow: 'hidden', minHeight: 178 }}>
          <Box sx={{ px: 1, py: 0.65, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, bgcolor: '#f8fafc', borderBottom: '1px solid #e2e8f0' }}>
            <Typography sx={{ fontSize: 11.5, color: sectionPalette.title, fontWeight: 950, textTransform: 'uppercase' }}>
              {sectionTitle}
            </Typography>
            {actionLabel ? (
              <Button size="small" onClick={onAction} sx={{ minWidth: 0, px: 0.9, py: 0.1, fontSize: 10.5, fontWeight: 900 }}>
                {actionLabel}
              </Button>
            ) : null}
          </Box>
          <Box sx={{ p: 0.9 }}>
            {children}
          </Box>
        </Box>
      );
    };

    return (
      <Stack spacing={1.3}>
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(5, minmax(0, 1fr))' }, gap: 1 }}>
          <FlowSummaryCard
            title="Solicitud"
            status={solicitudActual ? (authComplete ? 'Completa' : 'En proceso') : 'Sin solicitud'}
            tone={solicitudActual ? (authComplete ? 'ok' : 'warn') : 'default'}
            detail={solicitudActual
              ? `${fechaSolicitudVisible || 'Sin fecha'}${authComplete ? '' : ` - sigue ${nextPending?.USUARIO || 'autorizador'}`}`
              : 'No hay registro de solicitud.'}
          />
          <FlowSummaryCard
            title="Solicitar pago"
            status={reporteCount ? `Solicitada ${reporteCount} ${reporteCount === 1 ? 'vez' : 'veces'}` : 'Sin pedir'}
            tone={reporteCount ? 'ok' : 'default'}
            detail={reporteCount ? `${row?.REPORTE_SOLICITADO_FECHA || reportes?.[0]?.FECHA || 'Sin fecha'} - ${row?.REPORTE_SOLICITADO_USUARIO || reportes?.[0]?.USUARIO || 'Sistema'}` : 'No seleccionado en reporte.'}
            actionLabel={reporteCount ? 'Ver' : ''}
            onAction={() => onOpenDetail?.('reporte', row)}
          />
          <FlowSummaryCard
            title="Pagado"
            status={pagoCompleto ? 'Pagado' : 'Pendiente'}
            tone={pagoCompleto ? 'ok' : (solicitudActual ? 'warn' : 'default')}
            detail={pagoCompleto
              ? `${pagoFecha || 'Sin fecha'} - ${facturasPagadas.length || Number(row?.PAGO_ERP_COUNT || 0)} pago(s)`
              : (diasSinPago === null ? 'Sin fecha de solicitud.' : `${diasSinPago} dias desde la solicitud.`)}
            actionLabel={pagoCompleto || pagosErp.length ? 'Ver' : ''}
            onAction={() => onOpenDetail?.('pago', row)}
          />
          <FlowSummaryCard
            title="Comprobante"
            status={comprobantes.length ? `Cargado (${comprobantes.length})` : 'Sin comp'}
            tone={comprobantes.length ? 'ok' : 'default'}
            detail={comprobantes.length ? `${comprobanteFecha || 'Fecha no registrada'}` : 'Falta subir comprobante.'}
            actionLabel={comprobantes.length ? 'Ver' : ''}
            onAction={() => onOpenDetail?.('comprobante', row)}
          />
          <FlowSummaryCard
            title="Entrada"
            status={entradaCompleta ? 'Completa' : (entradaParcial ? 'Parcial' : 'Sin entrada')}
            tone={entradaCompleta ? 'ok' : (entradaParcial ? 'warn' : 'default')}
            detail={entradaCompleta ? (row?.ENTRADA_FECHA || 'Recibida') : (entradaParcial ? `${entradaPendientes} partida(s) pendiente(s)` : 'Sin registro')}
            actionLabel={entradaPartidas.length || entradas.length ? 'Ver' : ''}
            onAction={() => onOpenDetail?.('entrada', row)}
          />
        </Box>

        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', lg: 'repeat(2, minmax(0, 1fr))' }, gap: 1 }}>
          {renderFlowSection('Solicitud', solicitudActual ? (authComplete ? 'ok' : 'warn') : 'default', (
            solicitudActual ? (
              <Stack spacing={0.85}>
                <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(3, minmax(0, 1fr))', gap: 0.8 }}>
                  <Box>
                    <Typography sx={{ fontSize: 9.5, color: '#64748b', fontWeight: 900, textTransform: 'uppercase' }}>ID solicitud</Typography>
                    <Typography sx={{ fontSize: 12, color: '#0f172a', fontWeight: 950 }}>{selectedSolicitudId || '-'}</Typography>
                  </Box>
                  <Box>
                    <Typography sx={{ fontSize: 9.5, color: '#64748b', fontWeight: 900, textTransform: 'uppercase' }}>Fecha</Typography>
                    <Typography sx={{ fontSize: 12, color: '#0f172a', fontWeight: 950 }}>{fechaSolicitudVisible || 'Sin fecha'}</Typography>
                  </Box>
                  <Box>
                    <Typography sx={{ fontSize: 9.5, color: '#64748b', fontWeight: 900, textTransform: 'uppercase' }}>Monto</Typography>
                    <Typography sx={{ fontSize: 12, color: '#0f172a', fontWeight: 950 }}>{formatAdminMoney(montoSolicitudActual, solicitudActual?.divisa || row?.DIVISA)}</Typography>
                  </Box>
                </Box>
                <Box sx={{ border: '1px solid #e2e8f0', borderRadius: 1, overflow: 'hidden' }}>
                  <Box sx={{ px: 0.8, py: 0.5, bgcolor: '#f8fafc', borderBottom: '1px solid #e2e8f0' }}>
                    <Typography sx={{ fontSize: 10.5, color: '#475569', fontWeight: 950, textTransform: 'uppercase' }}>
                      Autorizadores
                    </Typography>
                  </Box>
                  <Box sx={{ maxHeight: 150, overflow: 'auto' }}>
                    {authFlow.length ? authFlow.map((item, index) => {
                      const ok = item?.ESTADO === 'CHECK_GREEN';
                      return (
                        <Box key={`${item.ROL}-${index}`} sx={{ px: 0.8, py: 0.55, display: 'grid', gridTemplateColumns: '20px 1fr auto', gap: 0.7, alignItems: 'center', borderBottom: index === authFlow.length - 1 ? 'none' : '1px solid #eef2f7' }}>
                          <Typography sx={{ fontSize: 10.5, color: '#64748b', fontWeight: 900 }}>{index + 1}</Typography>
                          <Box sx={{ minWidth: 0 }}>
                            <Typography sx={{ fontSize: 11.5, color: '#0f172a', fontWeight: 900, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                              {item.USUARIO || 'N/A'}
                            </Typography>
                            <Typography sx={{ fontSize: 10, color: '#64748b', fontWeight: 700 }}>
                              {item.FECHA_AUTORIZACION || (ok ? 'Sin fecha' : 'Pendiente')}
                            </Typography>
                          </Box>
                          {renderTinyChip(ok ? 'Autorizo' : 'Pendiente', ok ? 'ok' : 'warn')}
                        </Box>
                      );
                    }) : renderEmptyText('No hay flujo de autorizadores registrado.')}
                  </Box>
                </Box>
              </Stack>
            ) : renderEmptyText('No hay solicitud registrada para esta OC.')
          ))}

          {renderFlowSection('Pago', pagoCompleto || pagosErp.length ? 'ok' : (solicitudActual ? 'warn' : 'default'), (
            <Box sx={{ border: '1px solid #e2e8f0', borderRadius: 1, overflow: 'hidden' }}>
              <Typography sx={{ px: 0.8, py: 0.45, fontSize: 10.5, color: '#475569', fontWeight: 950, bgcolor: '#f8fafc', borderBottom: '1px solid #e2e8f0' }}>
                Facturas
              </Typography>
              {facturas.length ? (
                <Box sx={{ maxHeight: 202, overflow: 'auto' }}>
                  <Table size="small" stickyHeader sx={{ '& td, & th': { py: 0.42, px: 0.75, fontSize: 10.3, borderColor: '#eef2f7' } }}>
                    <TableHead>
                      <TableRow>
                        <TableCell>Factura</TableCell>
                        <TableCell>Fecha</TableCell>
                        <TableCell align="right">Total</TableCell>
                        <TableCell align="right">Abonado</TableCell>
                        <TableCell align="right">Saldo</TableCell>
                        <TableCell>Estatus</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {facturas.map((factura, index) => {
                        const folio = `${factura.SERIE_FACT || ''}${factura.FOLIO_FACT || ''}`.trim() || factura.UUID || 'Factura';
                        const pagada = Number(factura.SALDO || 0) <= 0.01 && Number(factura.ABONOS || 0) > 0;
                        return (
                          <TableRow key={`${folio}-${index}`}>
                            <TableCell sx={{ fontWeight: 900, maxWidth: 120, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{folio}</TableCell>
                            <TableCell>{factura.FECHA_FACT || '-'}</TableCell>
                            <TableCell align="right">{formatAdminMoney(factura.TOTAL, factura.DIVISA_FACT || row?.DIVISA)}</TableCell>
                            <TableCell align="right">{formatAdminMoney(factura.ABONOS, factura.DIVISA_FACT || row?.DIVISA)}</TableCell>
                            <TableCell align="right">{formatAdminMoney(factura.SALDO, factura.DIVISA_FACT || row?.DIVISA)}</TableCell>
                            <TableCell>{renderTinyChip(pagada ? 'Pagada' : 'Pendiente', pagada ? 'ok' : 'warn')}</TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </Box>
              ) : renderEmptyText(pagoCompleto ? 'Pago registrado sin detalle de facturas.' : 'Sin facturas pagadas registradas.')}
            </Box>
          ), pagoCompleto || pagosErp.length ? 'Ver detalle' : '', () => onOpenDetail?.('pago', row))}

          {renderFlowSection('Comprobante', comprobantes.length ? 'ok' : 'default', (
            comprobantes.length ? (
              <Box sx={{ maxHeight: 190, overflow: 'auto', display: 'grid', gap: 0.55 }}>
                {comprobantes.slice(0, 6).map((file, index) => (
                  <Box key={`${file.filename}-${index}`} sx={{ p: 0.75, border: '1px solid #e2e8f0', borderRadius: 1, bgcolor: index === 0 ? '#fff' : '#f8fafc', opacity: index === 0 ? 1 : 0.72 }}>
                    <Typography sx={{ fontSize: 11.5, color: '#0f172a', fontWeight: 950, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                      {file.filename || 'Comprobante'}
                    </Typography>
                    <Typography sx={{ fontSize: 10.5, color: '#64748b', fontWeight: 750 }}>
                      {file.fecha_alta || file.FECHA_ALTA || comprobanteFecha || 'Sin fecha'}
                    </Typography>
                  </Box>
                ))}
              </Box>
            ) : renderEmptyText('Falta subir comprobante.')
          ), comprobantes.length ? 'Ver detalle' : '', () => onOpenDetail?.('comprobante', row))}

          {renderFlowSection('Entrada', entradaCompleta ? 'ok' : (entradaParcial ? 'warn' : 'default'), (
            <Stack spacing={0.75}>
              <Box sx={{ px: 0.8, py: 0.65, border: `1px solid ${entradaCompleta ? '#bbf7d0' : entradaParcial ? '#fed7aa' : '#dbe4ef'}`, borderRadius: 1, bgcolor: entradaCompleta ? '#f0fdf4' : entradaParcial ? '#fff7ed' : '#f8fafc' }}>
                <Typography sx={{ fontSize: 11.5, color: entradaCompleta ? '#166534' : entradaParcial ? '#9a3412' : '#64748b', fontWeight: 950 }}>
                  {entradaCompleta ? 'Entrada completa' : entradaParcial ? 'Entrada parcial' : 'Sin entrada'}
                </Typography>
                <Typography sx={{ fontSize: 10.5, color: '#64748b', fontWeight: 750 }}>
                  {entradaCompleta ? 'Todas las partidas estan recibidas.' : entradaParcial ? `${entradaPendientes} partida(s) pendiente(s).` : 'Sin registro de entrada.'}
                </Typography>
              </Box>
              {entradaPartidas.length ? (
                <Box sx={{ maxHeight: 216, overflow: 'auto', border: '1px solid #e2e8f0', borderRadius: 1 }}>
                  <Table size="small" stickyHeader sx={{ tableLayout: 'fixed', '& td, & th': { py: 0.38, px: 0.75, fontSize: 10.3, borderColor: '#eef2f7' } }}>
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{ width: '42%' }}>Descripcion</TableCell>
                        <TableCell align="right" sx={{ width: '14%' }}>Cant. OC</TableCell>
                        <TableCell align="right" sx={{ width: '14%' }}>Recibido</TableCell>
                        <TableCell align="right" sx={{ width: '14%' }}>Pendiente</TableCell>
                        <TableCell sx={{ width: '16%' }}>Estatus</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {entradaPartidas.map((item, index) => {
                        const pendiente = Number(item.PENDIENTE || 0);
                        const descripcion = item.DESCRIPCION || '-';
                        return (
                          <TableRow key={`${item.CODIGO}-${index}`}>
                            <TableCell sx={{ fontWeight: 850, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                              <Tooltip title={descripcion} arrow disableInteractive>
                                <span>{descripcion}</span>
                              </Tooltip>
                            </TableCell>
                            <TableCell align="right">{Number(item.CANT_OC || 0).toLocaleString('en-US')}</TableCell>
                            <TableCell align="right" sx={{ color: '#059669', fontWeight: 900 }}>{Number(item.RECIBIDO || 0).toLocaleString('en-US')}</TableCell>
                            <TableCell align="right">{pendiente > 0 ? pendiente.toLocaleString('en-US') : '-'}</TableCell>
                            <TableCell>{renderTinyChip(pendiente > 0 ? 'Parcial' : 'Completo', pendiente > 0 ? 'warn' : 'ok')}</TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </Box>
              ) : renderEmptyText('Sin comparativo de partidas.')}
            </Stack>
          ), entradaPartidas.length || entradas.length ? 'Ver detalle' : '', () => onOpenDetail?.('entrada', row))}
        </Box>

        <Box sx={{ border: '1px solid #dbe4ef', borderRadius: 1.2, overflow: 'hidden', bgcolor: '#fff' }}>
          <Box sx={{ px: 1.2, py: 0.8, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, bgcolor: '#f8fafc', borderBottom: '1px solid #e2e8f0' }}>
            <Typography sx={{ fontSize: 12, color: '#0f172a', fontWeight: 950 }}>
              Solicitudes relacionadas
            </Typography>
            <Chip size="small" label={`${related.total || relatedRows.length} total`} sx={{ height: 20, fontSize: 10.5, fontWeight: 900, bgcolor: '#e0f2fe', color: '#075985' }} />
          </Box>
          <Box sx={{ maxHeight: 170, overflow: 'auto', display: 'grid', gap: 0.65, p: 0.8 }}>
            {loadingSolicitudesRelacionadas ? (
              <Box sx={{ py: 2, display: 'flex', justifyContent: 'center' }}><CircularProgress size={22} /></Box>
            ) : relatedRows.length ? relatedRows.map((item) => {
              const selected = Number(item.id) === selectedSolicitudId;
              return (
                <Box
                  key={item.id}
                  onClick={() => onSelectSolicitud?.(item)}
                  sx={{
                    p: 0.85,
                    border: selected ? '1px solid #93c5fd' : '1px solid #e2e8f0',
                    borderRadius: 1,
                    bgcolor: selected ? '#eff6ff' : (item.enRango ? '#fff' : '#f8fafc'),
                    cursor: 'pointer',
                    opacity: item.enRango ? 1 : 0.76,
                    '&:hover': { borderColor: '#93c5fd', bgcolor: '#eff6ff' },
                  }}
                >
                  <Stack direction="row" justifyContent="space-between" gap={1}>
                    <Typography sx={{ fontSize: 11.5, color: '#0f172a', fontWeight: 950 }}>
                      Solicitud #{item.id} - {item.fechaSolicitud || 'Sin fecha'}
                    </Typography>
                    <Stack direction="row" spacing={0.5}>
                      <Chip size="small" label={item.enRango ? 'En rango' : 'Fuera'} sx={{ height: 18, fontSize: 9.5, fontWeight: 900, bgcolor: item.enRango ? '#dcfce7' : '#e5e7eb', color: item.enRango ? '#166534' : '#475569' }} />
                      <Chip size="small" label={item.estatus || 'Default'} sx={{ height: 18, fontSize: 9.5, fontWeight: 900, bgcolor: '#e0f2fe', color: '#075985' }} />
                    </Stack>
                  </Stack>
                  <Typography sx={{ mt: 0.2, fontSize: 10.5, color: '#64748b', fontWeight: 700 }}>
                    {item.solicitante || 'Sin solicitante'} - {item.monto || '$0.00'} {item.divisa || row?.DIVISA || ''}
                  </Typography>
                </Box>
              );
            }) : (
              <Typography sx={{ p: 1.2, fontSize: 12, color: '#64748b', fontWeight: 700 }}>No hay solicitudes relacionadas.</Typography>
            )}
          </Box>
        </Box>
      </Stack>
    );
  };

  const renderEntrada = () => {
    const partidas = Array.isArray(data?.partidas) ? data.partidas : [];
    const entradas = Array.isArray(data?.entradas) ? data.entradas : [];
    const hasRecibido = entradas.length > 0 || partidas.some((item) => Number(item.RECIBIDO || 0) > 0);
    const pendientes = partidas.filter((item) => Number(item.PENDIENTE || 0) > 0).length;
    const entradaCompleta = partidas.length > 0 && pendientes === 0;
    return (
      <Stack spacing={1.4}>
        <Box sx={{ p: 1.1, border: '1px solid #dbe4ef', borderRadius: 1, bgcolor: entradaCompleta ? '#f0fdf4' : (hasRecibido ? '#fff7ed' : '#f8fafc') }}>
          <Typography sx={{ fontSize: 12.5, color: entradaCompleta ? '#166534' : (hasRecibido ? '#92400e' : '#64748b'), fontWeight: 900 }}>
            {entradaCompleta ? 'Entrada completa' : (hasRecibido ? 'Entrada parcial' : 'Sin entrada registrada')}
          </Typography>
          <Typography sx={{ mt: 0.25, fontSize: 11.5, color: '#64748b', fontWeight: 700 }}>
            {entradaCompleta ? 'Todas las partidas estan recibidas.' : (hasRecibido ? `${pendientes} ${pendientes === 1 ? 'partida pendiente' : 'partidas pendientes'} por recibir.` : 'Aun no hay cantidades recibidas para esta OC.')}
          </Typography>
        </Box>
        <Typography sx={{ fontSize: 12, color: '#005eb8', fontWeight: 900, textTransform: 'uppercase' }}>Comparativo partidas OC vs. recibido</Typography>
        <Box sx={{ border: '1px solid #dbe4ef', borderRadius: 1, overflow: 'auto', maxHeight: 320 }}>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ bgcolor: '#f8fafc' }}>
                {['#', 'Codigo', 'Descripcion', 'Cant. OC', 'Recibido', 'Pendiente', 'Estado'].map(h => (
                  <TableCell key={h} sx={{ fontSize: 10.5, color: '#64748b', fontWeight: 900 }}>{h}</TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {partidas.length ? partidas.map((item, index) => {
                const pendiente = Number(item.PENDIENTE || 0);
                const completo = pendiente <= 0;
                return (
                  <TableRow key={`${item.CODIGO}-${index}`}>
                    <TableCell sx={{ fontSize: 11 }}>{index + 1}</TableCell>
                    <TableCell sx={{ fontSize: 11, fontWeight: 800 }}>{item.CODIGO || '-'}</TableCell>
                    <TableCell sx={{ fontSize: 11 }}>{item.DESCRIPCION || '-'}</TableCell>
                    <TableCell align="right" sx={{ fontSize: 11 }}>{Number(item.CANT_OC || 0).toLocaleString('en-US')}</TableCell>
                    <TableCell align="right" sx={{ fontSize: 11, color: '#059669', fontWeight: 900 }}>{Number(item.RECIBIDO || 0).toLocaleString('en-US')}</TableCell>
                    <TableCell align="right" sx={{ fontSize: 11, color: pendiente > 0 ? '#d97706' : '#059669', fontWeight: 900 }}>{pendiente > 0 ? pendiente.toLocaleString('en-US') : '-'}</TableCell>
                    <TableCell><Chip size="small" label={completo ? 'Completo' : 'Parcial'} sx={{ height: 20, fontSize: 10, fontWeight: 900, bgcolor: completo ? '#dcfce7' : '#fef3c7', color: completo ? '#166534' : '#92400e' }} /></TableCell>
                  </TableRow>
                );
              }) : (
                <TableRow><TableCell colSpan={7} sx={{ py: 2, color: '#64748b', fontWeight: 700 }}>No hay partidas de entrada registradas.</TableCell></TableRow>
              )}
            </TableBody>
          </Table>
        </Box>
      </Stack>
    );
  };

  const renderReporte = () => {
    const reportes = Array.isArray(data?.reportes) ? data.reportes : [];
    return (
      <Stack spacing={1.2}>
        <Typography sx={{ fontSize: 12, color: '#64748b', fontWeight: 800 }}>
          {reportes.length ? `${reportes.length} ${reportes.length === 1 ? 'solicitud registrada' : 'solicitudes registradas'}` : 'No se ha solicitado pago en reporte.'}
        </Typography>
        <Box sx={{ maxHeight: 310, overflow: 'auto', pr: 0.5, display: 'grid', gap: 1 }}>
          {reportes.map((item, index) => (
            <Box key={`${item.LOTEID}-${index}`} sx={{ p: 1.1, border: '1px solid #dbe4ef', borderRadius: 1, bgcolor: '#fff' }}>
              <Stack direction="row" justifyContent="space-between" gap={2}>
                <Typography sx={{ fontSize: 12.5, color: '#0f172a', fontWeight: 900 }}>Reporte #{item.LOTEID}</Typography>
                <Typography sx={{ fontSize: 12, color: '#64748b', fontWeight: 800 }}>{item.FECHA}</Typography>
              </Stack>
              <Typography sx={{ mt: 0.35, fontSize: 11.5, color: '#64748b', fontWeight: 700 }}>Usuario: {item.USUARIO || 'Sistema'}</Typography>
              <Typography sx={{ mt: 0.2, fontSize: 11.5, color: '#64748b', fontWeight: 700 }}>Monto: {formatAdminMoney(item.MONTO, item.DIVISA || row?.DIVISA)}</Typography>
              {item.ARCHIVO && <Typography sx={{ mt: 0.2, fontSize: 11.5, color: '#64748b', fontWeight: 700 }}>Archivo: {item.ARCHIVO}</Typography>}
            </Box>
          ))}
        </Box>
      </Stack>
    );
  };

  const renderPago = () => {
    const resumen = data?.resumen || {};
    const pagosErp = Array.isArray(data?.pagosErp) ? data.pagosErp : [];
    const facturas = Array.isArray(data?.facturas) ? data.facturas : [];
    const tesoreros = Array.isArray(data?.tesoreros) ? data.tesoreros : [];
    const erpPagado = Number(resumen.saldoErp || 0) <= 0.01 && Number(resumen.abonosErp || 0) > 0;
    const facturasPagadas = facturas.filter(f => Number(f.SALDO || 0) <= 0.01 && Number(f.ABONOS || 0) > 0).length;
    return (
      <Stack spacing={1.4}>
        <Box sx={{ border: '1px solid #dbe4ef', borderRadius: 1, p: 1.3 }}>
          <Typography sx={{ mb: 0.8, color: '#005eb8', fontSize: 12, fontWeight: 900, textTransform: 'uppercase' }}>Resumen financiero</Typography>
          <DetailMetric label="Total OC" value={formatAdminMoney(resumen.totalOc, resumen.divisa)} />
          <DetailMetric label="Facturas vigentes" value={formatAdminMoney(resumen.facturasTotal, resumen.divisa)} />
          <DetailMetric label="Pagos ERP validos" value={formatAdminMoney(resumen.abonosErp, resumen.divisa)} tone={erpPagado ? 'ok' : 'default'} />
          <DetailMetric label="Saldo pendiente ERP" value={formatAdminMoney(resumen.saldoErp, resumen.divisa)} tone={Number(resumen.saldoErp || 0) > 0 ? 'warn' : 'ok'} />
          <DetailMetric label="Facturas pagadas" value={`${facturasPagadas} / ${facturas.length}`} tone={facturasPagadas === facturas.length && facturas.length > 0 ? 'ok' : 'warn'} />
        </Box>
        <Box sx={{ p: 1.1, border: '1px solid #dbe4ef', borderRadius: 1, bgcolor: erpPagado ? '#f0fdf4' : '#fff7ed' }}>
          <Typography sx={{ fontSize: 12.5, color: erpPagado ? '#166534' : '#92400e', fontWeight: 900 }}>
            {erpPagado ? 'Pagado en ERP' : 'Falta aplicar pago en ERP'}
          </Typography>
          <Typography sx={{ mt: 0.35, fontSize: 11.5, color: '#64748b', fontWeight: 700 }}>
            Tesorero encargado: {tesoreros.map(t => t.USER_NAME).join(', ') || 'Sin tesorero asignado'}
          </Typography>
        </Box>
        {facturas.length > 0 && (
          <Box sx={{ border: '1px solid #dbe4ef', borderRadius: 1, overflow: 'auto', maxHeight: 260 }}>
            <Table size="small">
              <TableHead><TableRow sx={{ bgcolor: '#f8fafc' }}>{['Factura', 'Fecha', 'Total', 'Abonado', 'Saldo', 'Estatus'].map(h => <TableCell key={h} sx={{ fontSize: 10.5, fontWeight: 900, color: '#64748b' }}>{h}</TableCell>)}</TableRow></TableHead>
              <TableBody>
                {facturas.map((factura, index) => {
                  const pagada = Number(factura.SALDO || 0) <= 0.01 && Number(factura.ABONOS || 0) > 0;
                  const folio = `${factura.SERIE_FACT || ''}${factura.FOLIO_FACT || ''}`.trim() || factura.UUID || '-';
                  return (
                    <TableRow key={`${folio}-${index}`}>
                      <TableCell sx={{ fontSize: 11, fontWeight: 900 }}>{folio}</TableCell>
                      <TableCell sx={{ fontSize: 11 }}>{factura.FECHA_FACT || '-'}</TableCell>
                      <TableCell align="right" sx={{ fontSize: 11, fontWeight: 800 }}>{formatAdminMoney(factura.TOTAL, factura.DIVISA_FACT || resumen.divisa)}</TableCell>
                      <TableCell align="right" sx={{ fontSize: 11, fontWeight: 800 }}>{formatAdminMoney(factura.ABONOS, factura.DIVISA_FACT || resumen.divisa)}</TableCell>
                      <TableCell align="right" sx={{ fontSize: 11, fontWeight: 800 }}>{formatAdminMoney(factura.SALDO, factura.DIVISA_FACT || resumen.divisa)}</TableCell>
                      <TableCell><Chip size="small" label={pagada ? 'Pagada' : 'Pendiente'} sx={{ height: 20, fontSize: 10, fontWeight: 900, bgcolor: pagada ? '#dcfce7' : '#fef3c7', color: pagada ? '#166534' : '#92400e' }} /></TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </Box>
        )}
        {pagosErp.length > 0 && (
          <Box sx={{ border: '1px solid #dbe4ef', borderRadius: 1, overflow: 'auto', maxHeight: 220 }}>
            <Table size="small">
              <TableHead><TableRow sx={{ bgcolor: '#f8fafc' }}>{['Recibo', 'Fecha', 'Forma', 'Referencia', 'Importe'].map(h => <TableCell key={h} sx={{ fontSize: 10.5, fontWeight: 900, color: '#64748b' }}>{h}</TableCell>)}</TableRow></TableHead>
              <TableBody>
                {pagosErp.map((pago, index) => (
                  <TableRow key={`${pago.FOLIO_RECIBO}-${index}`}>
                    <TableCell sx={{ fontSize: 11, fontWeight: 800 }}>{pago.FOLIO_RECIBO || '-'}</TableCell>
                    <TableCell sx={{ fontSize: 11 }}>{pago.FECHA_PAGO || '-'}</TableCell>
                    <TableCell sx={{ fontSize: 11 }}>{pago.FORMA_PAGO || '-'}</TableCell>
                    <TableCell sx={{ fontSize: 11 }}>{pago.REFERENCIA || '-'}</TableCell>
                    <TableCell align="right" sx={{ fontSize: 11, fontWeight: 900 }}>{formatAdminMoney(pago.IMPORTE_PAGO, pago.DIVISA || resumen.divisa)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Box>
        )}
      </Stack>
    );
  };

  const renderComprobante = () => {
    const comprobantes = Array.isArray(data?.comprobantes) ? data.comprobantes : [];
    const tesoreros = Array.isArray(data?.tesoreros) ? data.tesoreros : [];

    const hasComprobante = comprobantes.length > 0;
    const solicitudActivaId = Number(data?.solicitudActivaId || 0);

    // Separar los del solicitud activa (arriba) del resto (abajo como relacionados)
    const archivosActivos = solicitudActivaId
      ? comprobantes.filter(f => Number(f.solicitud_id || f.SOLICITUD_ID || 0) === solicitudActivaId)
      : [];
    const archivosRelacionados = solicitudActivaId
      ? comprobantes.filter(f => Number(f.solicitud_id || f.SOLICITUD_ID || 0) !== solicitudActivaId)
      : comprobantes;

    // Si no hay archivos de la solicitud activa, mostrar todos como activos
    const filesToShow = archivosActivos.length > 0 ? archivosActivos : comprobantes;
    const relatedToShow = archivosActivos.length > 0 ? archivosRelacionados : [];

    const buildComprobanteLinks = (file) => {
      const archivoId = file.archivo_id || file.ARCHIVO_ID;
      const origen = String(file.origen || file.ORIGEN || '').toUpperCase();
      const rawId = String(archivoId || '');
      const comprobanteId = rawId.replace(/^COMP_NEW_/, '').replace(/^COMP_OLD_/, '');
      const esComprobanteDirecto = origen === 'NUEVO' || origen === 'VIEJO' || rawId.startsWith('COMP_NEW_') || rawId.startsWith('COMP_OLD_');
      const comprobanteOrigen = origen === 'VIEJO' || rawId.startsWith('COMP_OLD_') ? 'VIEJO' : 'NUEVO';
      return {
        previewUrl: archivoId
          ? (esComprobanteDirecto
            ? `${apiUrl}/solicitudesAdmin/visualizarComprobante/${comprobanteId}?origen=${comprobanteOrigen}`
            : `${apiUrl}/solicitudesAdmin/visualizarArchivo/${archivoId}`)
          : '',
        downloadUrl: archivoId
          ? (esComprobanteDirecto
            ? `${apiUrl}/solicitudesAdmin/descargarComprobante/${comprobanteId}?origen=${comprobanteOrigen}`
            : `${apiUrl}/solicitudesAdmin/descargarArchivo/${archivoId}`)
          : '',
      };
    };

    const renderArchivoRow = (file, index, muted = false) => {
      const { previewUrl, downloadUrl } = buildComprobanteLinks(file);
      const solicitudId = file.solicitud_id || file.SOLICITUD_ID;
      return (
        <TableRow
          key={`${file.filename}-${solicitudId || 's'}-${index}-${muted ? 'rel' : 'act'}`}
          sx={{
            bgcolor: muted ? '#f8fafc' : '#fff',
            opacity: muted ? 0.74 : 1,
            '&:hover': { bgcolor: muted ? '#f1f5f9' : '#f8fafc' },
          }}
        >
          <TableCell sx={{ fontSize: 11, fontWeight: muted ? 800 : 900, maxWidth: 420 }}>
            <Tooltip title={file.filename || ''} arrow disableInteractive>
              <Typography sx={{ fontSize: 11, fontWeight: muted ? 800 : 900, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                {file.filename || '-'}
              </Typography>
            </Tooltip>
            {muted && (
              <Typography sx={{ mt: 0.15, fontSize: 10, color: '#64748b', fontWeight: 800 }}>
                Relacionado{solicitudId ? ` · Solicitud ${solicitudId}` : ''}
              </Typography>
            )}
          </TableCell>
          <TableCell align="right">
            <Stack direction="row" spacing={0.5} justifyContent="flex-end">
              <Tooltip title="Visualizar" arrow>
                <span>
                  <IconButton size="small" disabled={!previewUrl} onClick={() => window.open(previewUrl, '_blank', 'noopener,noreferrer')} sx={{ width: 28, height: 28, color: muted ? '#64748b' : '#005eb8' }}>
                    <VisibilityIcon sx={{ fontSize: 17 }} />
                  </IconButton>
                </span>
              </Tooltip>
              <Tooltip title="Descargar" arrow>
                <span>
                  <IconButton size="small" disabled={!downloadUrl} onClick={() => window.open(downloadUrl, '_blank', 'noopener,noreferrer')} sx={{ width: 28, height: 28, color: muted ? '#64748b' : '#15803d' }}>
                    <DownloadIcon sx={{ fontSize: 17 }} />
                  </IconButton>
                </span>
              </Tooltip>
            </Stack>
          </TableCell>
        </TableRow>
      );
    };

    return (
      <Stack spacing={1.3}>
        <Box sx={{ p: 1.1, border: '1px solid #dbe4ef', borderRadius: 1, bgcolor: hasComprobante ? '#f0fdf4' : '#fff7ed' }}>
          <Typography sx={{ fontSize: 12.5, color: hasComprobante ? '#166534' : '#92400e', fontWeight: 900 }}>
            {hasComprobante ? 'Comprobante registrado' : 'Falta subir comprobante'}
          </Typography>
          <Typography sx={{ mt: 0.35, fontSize: 11.5, color: '#64748b', fontWeight: 700 }}>
            Tesorero encargado: {tesoreros.map(t => t.USER_NAME).join(', ') || 'Sin tesorero asignado'}
          </Typography>
        </Box>
        <Box sx={{ border: '1px solid #dbe4ef', borderRadius: 1, overflow: 'auto', maxHeight: 260 }}>
          <Table size="small">
            <TableHead><TableRow sx={{ bgcolor: '#f8fafc' }}><TableCell sx={{ fontSize: 10.5, fontWeight: 900, color: '#64748b' }}>Comprobante</TableCell><TableCell align="right" sx={{ fontSize: 10.5, fontWeight: 900, color: '#64748b' }}>Acciones</TableCell></TableRow></TableHead>
            <TableBody>
              {comprobantes.length ? (
                <>
                  {filesToShow.map((file, index) => renderArchivoRow(file, index))}
                  {relatedToShow.length > 0 && (
                    <TableRow>
                      <TableCell colSpan={2} sx={{ py: 0.65, bgcolor: '#f8fafc', borderTop: '1px solid #e2e8f0', borderBottom: '1px solid #e2e8f0' }}>
                        <Typography sx={{ fontSize: 10.5, color: '#64748b', fontWeight: 900, textTransform: 'uppercase' }}>
                          Comprobantes relacionados
                        </Typography>
                      </TableCell>
                    </TableRow>
                  )}
                  {relatedToShow.map((file, index) => renderArchivoRow(file, index, true))}
                </>
              ) : (
                <TableRow><TableCell colSpan={2} sx={{ py: 2, color: '#64748b', fontWeight: 700 }}>No hay comprobante registrado.</TableCell></TableRow>
              )}
            </TableBody>
          </Table>
        </Box>
      </Stack>
    );
  };

  const renderContent = () => {
    if (loading) {
      return <Box sx={{ py: 5, display: 'flex', justifyContent: 'center' }}><CircularProgress size={32} /></Box>;
    }
    if (type === 'solicitud') return renderSolicitud();
    if (type === 'entrada') return renderEntrada();
    if (type === 'reporte') return renderReporte();
    if (type === 'comprobante') return renderComprobante();
    if (type === 'pago' || type === 'pagoErp') return renderPago();
    return <Typography sx={{ color: '#64748b', fontWeight: 700 }}>Sin detalle disponible.</Typography>;
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth disableRestoreFocus PaperProps={{ sx: { borderRadius: 2 } }}>
      <DialogTitle sx={{ py: 1.4, px: 2, borderBottom: '1px solid #e2e8f0', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Box>
          <Typography sx={{ fontSize: 15, color: '#0f172a', fontWeight: 900 }}>{title}</Typography>
          <Typography sx={{ mt: 0.25, fontSize: 11.5, color: '#64748b', fontWeight: 700 }}>
            OC {row?.FOLIO_OC || row?.FOLIO || row?.OC || data?.solicitud?.solicitud?.folioOc || data?.folioOc || '-'} · {row?.PROVEEDOR || row?.EMPRESA || data?.solicitud?.solicitud?.proveedor || '-'}
          </Typography>
        </Box>
        <IconButton size="small" onClick={onClose} sx={{ color: '#64748b' }}>
          <ClearIcon fontSize="small" />
        </IconButton>
      </DialogTitle>
      <DialogContent sx={{ p: 1.5, bgcolor: '#fcfdff' }}>
        {renderContent()}
      </DialogContent>
    </Dialog>
  );
};

const AdminMobileTotalsFooter = React.memo(function AdminMobileTotalsFooter({ divisas, totalCount }) {
  const [expanded, setExpanded] = useState(false);
  if (!divisas || Object.keys(divisas).length === 0) return null;

  const displayTotalCount = totalCount || 0;
  const fmt = (n) => (n || 0).toLocaleString('es-MX', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

  const getBadgeStyle = (div) => {
    if (div === 'USD') return { color: '#b45309', bg: '#fef3c7' };
    if (div === 'EUR') return { color: '#4338ca', bg: '#e0e7ff' };
    if (div === 'MXN') return { color: '#0369a1', bg: '#e0f2fe' };
    if (div === 'GBP') return { color: '#701a75', bg: '#fae8ff' };
    if (div === 'CAD') return { color: '#991b1b', bg: '#fee2e2' };
    return { color: '#334155', bg: '#f1f5f9' };
  };

  const getCurrencySymbol = (div) => {
    if (div === 'EUR') return '€';
    if (div === 'GBP') return '£';
    return '$';
  };

  const divisasEntries = Object.entries(divisas || {}).sort((a, b) => {
    const order = { MXN: 1, USD: 2, EUR: 3 };
    return (order[a[0]] || 99) - (order[b[0]] || 99);
  });

  return (
    <Paper
      elevation={3}
      sx={{
        mt: 'auto',
        pt: 0,
        position: 'sticky',
        bottom: 2,
        zIndex: 10,
        borderRadius: '14px',
        border: '1.5px solid #cbd5e1',
        bgcolor: '#ffffff',
        boxShadow: '0 4px 18px rgba(15, 23, 42, 0.12)',
        overflow: 'hidden',
      }}
    >
      <Box
        onClick={() => setExpanded(prev => !prev)}
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          px: 1.4,
          py: 0.8,
          bgcolor: '#f1f5f9',
          borderBottom: expanded ? '1px solid #e2e8f0' : 'none',
          cursor: 'pointer',
          userSelect: 'none',
          '&:active': { bgcolor: '#e2e8f0' }
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8 }}>
          <Typography sx={{ fontWeight: 900, fontSize: '0.74rem', color: '#0f172a', letterSpacing: '0.02em' }}>
            TOTALES ({displayTotalCount} OCs)
          </Typography>
          <Chip
            label={`${divisasEntries.length} Divisas`}
            size="small"
            sx={{ height: 18, fontSize: '0.62rem', fontWeight: 850, bgcolor: '#e0f2fe', color: '#0369a1' }}
          />
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.4 }}>
          <Typography sx={{ fontSize: '0.68rem', fontWeight: 700, color: '#0284c7' }}>
            {expanded ? 'Ocultar' : 'Ver detalle'}
          </Typography>
          <IconButton size="small" sx={{ p: 0.1, color: '#0284c7' }}>
            {expanded ? <ExpandLessIcon sx={{ fontSize: 18 }} /> : <ExpandMoreIcon sx={{ fontSize: 18 }} />}
          </IconButton>
        </Box>
      </Box>

      {/* Resumen en 1 sola fila cuando está comprimido */}
      {!expanded && (
        <Box
          onClick={() => setExpanded(true)}
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1.2,
            px: 1.4,
            py: 0.7,
            overflowX: 'auto',
            scrollbarWidth: 'none',
            '&::-webkit-scrollbar': { display: 'none' },
            bgcolor: '#ffffff',
            cursor: 'pointer'
          }}
        >
          {divisasEntries.map(([divisa, vals]) => {
            const { color, bg } = getBadgeStyle(divisa);
            const sym = getCurrencySymbol(divisa);
            return (
              <Box key={divisa} sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexShrink: 0 }}>
                <Chip
                  label={divisa}
                  size="small"
                  sx={{ height: 16, fontSize: '0.58rem', fontWeight: 850, bgcolor: bg, color: color }}
                />
                <Typography sx={{ fontSize: '0.74rem', fontWeight: 850, color: '#0f172a', whiteSpace: 'nowrap' }}>
                  {sym}{fmt(vals.total || 0)}
                </Typography>
              </Box>
            );
          })}
        </Box>
      )}

      {/* Desglose completo cuando está expandido */}
      <Collapse in={expanded} timeout="auto" unmountOnExit>
        <Box sx={{ p: 1.2, bgcolor: '#f8fafc', display: 'flex', flexDirection: 'column', gap: 1 }}>
          {divisasEntries.map(([divisa, vals]) => {
            const total = vals.total || 0;
            const sol = vals.solicitado || 0;
            const rest = vals.restante !== undefined ? vals.restante : (total > sol ? total - sol : 0);
            const { color, bg } = getBadgeStyle(divisa);
            const sym = getCurrencySymbol(divisa);

            return (
              <Box
                key={divisa}
                sx={{
                  p: 1,
                  bgcolor: '#ffffff',
                  borderRadius: '10px',
                  border: '1px solid #e2e8f0',
                  boxShadow: '0 1px 3px rgba(0,0,0,0.03)'
                }}
              >
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.6 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.6 }}>
                    <Chip
                      label={divisa}
                      size="small"
                      sx={{ height: 18, fontSize: '0.62rem', fontWeight: 900, bgcolor: bg, color: color }}
                    />
                    <Typography sx={{ fontSize: '0.68rem', fontWeight: 700, color: '#64748b' }}>
                      {vals.count || 0} OCs
                    </Typography>
                  </Box>
                  <Typography sx={{ fontSize: '0.78rem', fontWeight: 900, color: '#0f172a' }}>
                    {sym}{fmt(total)}
                  </Typography>
                </Box>

                <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 0.8, pt: 0.6, borderTop: '1px dashed #e2e8f0' }}>
                  <Box>
                    <Typography sx={{ fontSize: '0.58rem', fontWeight: 750, color: '#64748b', textTransform: 'uppercase' }}>
                      Solicitado
                    </Typography>
                    <Typography sx={{ fontSize: '0.72rem', fontWeight: 800, color: '#16a34a' }}>
                      {sym}{fmt(sol)}
                    </Typography>
                  </Box>
                  <Box sx={{ textAlign: 'right' }}>
                    <Typography sx={{ fontSize: '0.58rem', fontWeight: 750, color: '#64748b', textTransform: 'uppercase' }}>
                      Restante
                    </Typography>
                    <Typography sx={{ fontSize: '0.72rem', fontWeight: 800, color: '#ea580c' }}>
                      {sym}{fmt(rest)}
                    </Typography>
                  </Box>
                </Box>
              </Box>
            );
          })}
        </Box>
      </Collapse>
    </Paper>
  );
});

export default function SolicitudesAdmin() {
  const { loggedInUsername, nombreEmpresa, idEmpresa, idPerfil, nombrePerfil } = useSession();
  const apiUrl = config.apiBaseUrl;

  const usuarioId = useMemo(() => {
    return Number(sessionStorage.getItem('UsuarioId') || 0);
  }, []);

  const esTesorero = useMemo(() => {
    return typeof nombrePerfil === 'string' && 
      nombrePerfil.toLowerCase().includes('tesorero');
  }, [nombrePerfil]);



  const empresaScope = useMemo(() => buildEmpresaScope(idEmpresa), [idEmpresa]);

  const empresaIds = useMemo(() => {
    if (!empresaScope || empresaScope === '0') return [];
    return empresaScope
      .split(',')
      .map((n) => Number(n))
      .filter((x) => Number.isFinite(x) && x > 0);
  }, [empresaScope]);

  const empresaIdPersist = useMemo(() => {
    const arr = normalizeEmpresaArray(idEmpresa);
    return arr.length === 1 ? arr[0] : 0;
  }, [idEmpresa]);

  const hiddenFieldsMemo = useMemo(() => {
    const base = [
      'DIAS_VENCIMIENTO', 'DIAS_VENCIMINETO', 'DIASRESTANTES', 'DIAS_VENCIMIENTO_RAW',
      'VENCIMIENTO', 'REFERENCIA', 'cadenaAut', 'CANCELADO_ERP', 'usuarioCancelo',
      'ID_OC', 'HAS_OC', 'ID_CB', 'HAS_CB', 'ID_FACTURA', 'HAS_FACTURA',
      'USUARIO_CANCELO', 'OBSERV_CANCELADAS', 'FECHA_CANCELACION',
      'ID_SOLICITUD', 'IDDATOSERP', 'SOLICITUDES_COUNT', 'PAGADAS_COUNT',
      'TIENE_SOLICITUD', 'TIENE_PAGO', 'ULTIMA_SOLICITUD', 'ULTIMO_PAGO',
      'REPORTE_SOLICITADO_COUNT', 'REPORTE_SOLICITADO_USUARIO', 'REPORTE_SOLICITADO_FECHA',
      'REPORTE_SOLICITADO_EVENTOS',
      'ENTRADAS_COUNT', 'ENTRADAS_TOTAL', 'ENTRADA_FECHA', 'ENTRADA_FOLIOS', 'ENTRADA_COMPLETA',
      'PAGO_ERP_COUNT', 'PAGO_ERP_TOTAL', 'PAGO_ERP_ABONOS', 'PAGO_ERP_SALDO',
      'PAGO_ERP_FECHA', 'PAGO_ERP_RECIBOS', 'PAGO_ERP_COMPLETO',
      'COMPROBANTE_COUNT', 'TIENE_COMPROBANTE',
    ];

    if (esTesorero) {
      return [
        ...base,
      ];
    }

    return [...base];
  }, [esTesorero]);

  const exportHiddenFieldsMemo = useMemo(() => {
    return hiddenFieldsMemo.filter(f => f !== 'ID_SOLICITUD' && f !== 'OBSERVACIONES' && f !== 'FACTURAS_ASOCIADAS');
  }, [hiddenFieldsMemo]);

  const compactTableHiddenFields = useMemo(() => ([
    'ID', 'ID_SOLICITUD', 'IDDATOSERP', 'EMPRESAID', 'EMPRESA', 'REQUI', 'FOLIO', 'FOLIO_OC', 'FECHA_OC', 'FECHASOLICITUD',
    'FECHA_REQUERIDA', 'TOTAL', 'TOTAL_OC', 'DIVISA', 'PAGO', 'TIPO_PAGO', 'TIPOPAGO', 'TIPO_DE_PAGO', 'MONTOSOLICITADO',
    'NUMEROCC', 'CENTROCOSTO', 'CENTRO_COSTO', 'NUMERO_ECONOMICO', 'ECONOMICO', 'PROVEEDOR', 'RFC', 'ESTATUS',
    'DEPARTAMENTO', 'SOLICITANTE', 'REQUISITOR', 'COMPRADOR', 'FOLIO_FACTURA', 'PENDIENTE', 'CONCEPTOPAGO', 'NOTA',
    'NOTAS_REQUI', 'FECHA_PAGO', 'MONTO_DIVISA', 'FACTURA_COMPACTA', 'FACTURAS_ASOCIADAS', 'DESCRIPCION',
    'SOLICITUDES_COUNT', 'PAGADAS_COUNT', 'TIENE_SOLICITUD', 'TIENE_PAGO', 'ULTIMA_SOLICITUD', 'ULTIMO_PAGO', 'USUARIO_PAGO',
    'REPORTE_SOLICITADO_COUNT', 'REPORTE_SOLICITADO_USUARIO', 'REPORTE_SOLICITADO_FECHA', 'REPORTE_SOLICITADO_EVENTOS',
    'ENTRADAS_COUNT', 'ENTRADAS_TOTAL', 'ENTRADA_FECHA', 'ENTRADA_FOLIOS', 'ENTRADA_COMPLETA',
    'PAGO_ERP_COUNT', 'PAGO_ERP_TOTAL', 'PAGO_ERP_ABONOS', 'PAGO_ERP_SALDO', 'PAGO_ERP_FECHA', 'PAGO_ERP_RECIBOS', 'PAGO_ERP_COMPLETO',
    'PAGO_OPERATIVO_COUNT', 'PAGO_OPERATIVO_TOTAL', 'PAGO_OPERATIVO_ABONOS', 'PAGO_OPERATIVO_FECHA', 'PAGO_OPERATIVO_COMPLETO', 'PAGO_OPERATIVO_ORIGEN',
    'COMPROBANTE_COUNT', 'TIENE_COMPROBANTE', 'COMPROBANTE_FECHA', 'FECHA_SOLICITUD_PAGO', 'cadenaAut',
    'HAS_FACTURA', 'ID_FACTURA', 'HAS_CB', 'ID_CB', 'HAS_OC', 'ID_OC', 'EXHIBICION',
    'AUTNIV1', 'AUTNIV2', 'AUTNIV3', 'AUTNIV4', 'AUTNIV5',
    'AUTNIV1_NOMBRE', 'AUTNIV1_FECHA', 'AUTNIV2_NOMBRE', 'AUTNIV2_FECHA',
    'AUTNIV3_NOMBRE', 'AUTNIV3_FECHA', 'AUTNIV4_NOMBRE', 'AUTNIV4_FECHA',
    'AUTNIV5_NOMBRE', 'AUTNIV5_FECHA',
    '__hideCompactId', '__disableStatusRowColor',
  ]), []);

  const tableHiddenFieldsMemo = useMemo(() => ([
    ...hiddenFieldsMemo,
    ...compactTableHiddenFields,
  ]), [hiddenFieldsMemo, compactTableHiddenFields]);

  // ✅ Labels estables para la tabla (no dinámicos)
  const tableColumnLabels = useMemo(() => ({
    EXHIBIT: 'Exhibit',
    EXHIBICION: 'Exhibición',
    OC_REQUI: 'OC / Requi',
    FECHA_SOLICITANTE: 'Fecha OC / Requisitor',
    PROVEEDOR_COMPRADOR: 'Proveedor / Comprador',
    CENTRO_DEPTO: 'Centro / Departamento',
    TOTAL_DIVISA: 'Total / Divisa',
    MONTO_RESTANTE: 'Solicitado / Rest.',
    FACTURA_COMPACTA: 'Factura',
    SOLICITUD_RESUMEN: 'Solicitud',
    REPORTE_SOLICITADO_RESUMEN: 'Solicitar pago',
    ENTRADA_RESUMEN: 'Entrada',
    PAGO_ERP_RESUMEN: 'Comprobante',
    PAGO_RESUMEN: 'Pagado',
    AUTNIV1: 'Aut N1',
    AUTNIV2: 'Aut N2',
    AUTNIV3: 'Aut N3',
    AUTNIV4: 'Aut N4',
    AUTNIV5: 'Aut N5',
    FOLIO: 'Orden',
    FECHASOLICITUD: 'Fecha OC',
    NUMEROCC: 'Centro costo',
    CENTROCOSTO: 'Nombre CC',
    DEPARTAMENTO: 'Departamento',
    SOLICITANTE: 'Comprador',
    REQUISITOR: 'Requisitor',
    FOLIO_FACTURA: 'Factura',
    NUMERO_ECONOMICO: 'Economico',
    TOTAL: 'Total OC',
    MONTOSOLICITADO: 'Programar a Pago',
    CONCEPTOPAGO: 'Condicion Pago',
    NOTA: 'Nota Requi',
    METODOPAGO: 'Metodo Pago',
    REQUI: 'Requi',
    ESTATUS: 'Estado',
    FECHAPAGO: 'Fecha Pago',
  }), []);

  const compactColumnOrder = useMemo(() => ([
    'OC_REQUI',
    'FECHA_SOLICITANTE',
    'PROVEEDOR_COMPRADOR',
    'CENTRO_DEPTO',
    'TOTAL_DIVISA',
    'MONTO_RESTANTE',
    'EXHIBIT',
    'SOLICITUD_RESUMEN',
    'REPORTE_SOLICITADO_RESUMEN',
    'ENTRADA_RESUMEN',
    'PAGO_ERP_RESUMEN',
    'PAGO_RESUMEN',
  ]), []);

  const [data, setData] = useState({ contenido: [], total: 0 });
  const [empresaOptions, setEmpresaOptions] = useState([]);
  const [filterOptionRows, setFilterOptionRows] = useState([]);
  const [loadingData, setLoadingData] = useState(true);
  const [loadingAction, setLoadingAction] = useState(false);
  const [error, setError] = useState(null);
  const [selectedRow] = useState(null);
  const [showFiltersPanel, setShowFiltersPanel] = useState(false);
  const [filterAnchorEl, setFilterAnchorEl] = useState(null);
  const [popoverAnchorEl, setPopoverAnchorEl] = useState(null);
  const [popoverActiveField, setPopoverActiveField] = useState(null);

  const [solicitudesRelacionadas, setSolicitudesRelacionadas] = useState({ solicitudes: [], total: 0, dentroRango: 0, fueraRango: 0 });
  const [loadingSolicitudesRelacionadas, setLoadingSolicitudesRelacionadas] = useState(false);
  const [statusDetail, setStatusDetail] = useState({
    open: false,
    type: '',
    row: null,
    loading: false,
    data: null,
  });



  const [modalStates, setModalStates] = useState({
    openCancelacionModal: false,
    observaciones: '',
    cancelarID: null,
    cancelarEmpresa: null,
    diasRestantes: null,
    folio: '',
    proveedor: '',
    fechaSolicitud: '',
    monto: '',
    fechaVencimiento: '',
    departamento: '',
    centroCostos: '',
  });

  const [fileUploadModalOpen, setFileUploadModalOpen] = useState(false);
  const [adjuntos, setAdjuntos] = useState([]);
  const defaultMonthRange = useMemo(() => getCurrentMonthRange(), []);



  const [proyectosCatalogo, setProyectosCatalogo] = useState([]);
  const [divisionesCatalogo, setDivisionesCatalogo] = useState([]);
  const [centrosCostoCatalogo, setCentrosCostoCatalogo] = useState([]);
  const [hierarchicalAnchorEl, setHierarchicalAnchorEl] = useState(null);

  const baseFilters = useMemo(() => {
    const initialEmpId = empresaIds.length === 1 ? empresaIds[0] : null;
    const initialEmpName = initialEmpId ? nombreEmpresa : '';

    return {
      FECHA_INICIO: defaultMonthRange.start,
      FECHA_FIN: defaultMonthRange.end,
      FOLIO: '',
      EMPRESA: initialEmpName,
      EMPRESA_IDS: initialEmpId ? [initialEmpId] : [],
      PROYECTOS: [],
      PROYECTO: '',
      DIVISIONES: [],
      DIVISION: '',
      PROVEEDOR: '',
      PROVEEDORES: [],
      DIVISAS: [],
      CENTRO_COSTOS: [],
      DEPARTAMENTOS: [],
      ESTATUS: '',
    };
  }, [defaultMonthRange, empresaIds, nombreEmpresa]);

  const [tempFilterData, setTempFilterData] = useState(baseFilters);
  const [filterData, setFilterData] = useState(baseFilters);
  const [quickSearch, setQuickSearch] = useState('');


  // ✅ Unificación de estados de la tabla (idéntico a SolicitudesProgramadas)
  const [tableParams, setTableParams] = useState({
    page: 1,
    pageSize: 25,
    globalQuery: '',
    sortField: 'FECHASOLICITUD',
    sortDir: 'DESC'
  });
  const tableParamsRef = useRef(tableParams);
  tableParamsRef.current = tableParams;
  const [hiddenCols] = useState([]);

  // Información formateada de los filtros activos para cuando no hay resultados (Empty State)
  const emptyStateData = useMemo(() => {

    const activeFiltersList = [];

    const formatFmt = (strDate) => {
      if (!strDate) return '';
      const parts = strDate.split('-');
      return parts.length === 3 ? `${parts[2]}/${parts[1]}/${parts[0]}` : strDate;
    };

    const dateRangeStr = (filterData.FECHA_INICIO && filterData.FECHA_FIN)
      ? `${formatFmt(filterData.FECHA_INICIO)} al ${formatFmt(filterData.FECHA_FIN)}`
      : '';

    if (filterData.FOLIO) activeFiltersList.push(`Folio/Requisición: "${filterData.FOLIO}"`);
    
    if (Array.isArray(filterData.DEPARTAMENTOS) && filterData.DEPARTAMENTOS.length > 0) {
      activeFiltersList.push(`Departamento: ${filterData.DEPARTAMENTOS.join(', ')}`);
    } else if (filterData.DEPARTAMENTO) {
      activeFiltersList.push(`Departamento: ${filterData.DEPARTAMENTO}`);
    }

    if (Array.isArray(filterData.CENTRO_COSTOS) && filterData.CENTRO_COSTOS.length > 0) {
      activeFiltersList.push(`Centro de Costo: ${filterData.CENTRO_COSTOS.join(', ')}`);
    } else if (filterData.CENTRO_COSTO) {
      activeFiltersList.push(`Centro de Costo: ${filterData.CENTRO_COSTO}`);
    }

    if (Array.isArray(filterData.PROVEEDORES) && filterData.PROVEEDORES.length > 0) {
      activeFiltersList.push(`Proveedor: ${filterData.PROVEEDORES.join(', ')}`);
    } else if (filterData.PROVEEDOR) {
      activeFiltersList.push(`Proveedor: ${filterData.PROVEEDOR}`);
    }

    if (Array.isArray(filterData.DIVISAS) && filterData.DIVISAS.length > 0) {
      activeFiltersList.push(`Divisa: ${filterData.DIVISAS.join(', ')}`);
    } else if (filterData.DIVISA) {
      activeFiltersList.push(`Divisa: ${filterData.DIVISA}`);
    }

    if (filterData.ESTATUS) {
      activeFiltersList.push(`Estatus: "${filterData.ESTATUS}"`);
    }

    if (tableParams.globalQuery) {
      activeFiltersList.push(`Búsqueda rápida: "${tableParams.globalQuery}"`);
    }

    return {
      dateRangeStr,
      activeFiltersList
    };
  }, [filterData, tableParams.globalQuery]);



  const userEmpresaIds = useMemo(() => {
    if (empresaOptions.length) {
      return empresaOptions.map(e => e.id);
    }
    return empresaIds;
  }, [empresaOptions, empresaIds]);

  const selectedEmpresaIds = useMemo(() => {
    if (Array.isArray(filterData.EMPRESA_IDS) && filterData.EMPRESA_IDS.length > 0) {
      const selected = filterData.EMPRESA_IDS.map(Number).filter(id => Number.isInteger(id) && id > 0);
      return selected;
    }
    return empresaIds.length ? empresaIds : userEmpresaIds;
  }, [empresaIds, userEmpresaIds, filterData.EMPRESA_IDS]);

  const selectedCatalogEmpresaIds = useMemo(() => {
    if (Array.isArray(tempFilterData.EMPRESA_IDS) && tempFilterData.EMPRESA_IDS.length > 0) {
      const selected = tempFilterData.EMPRESA_IDS.map(Number).filter(id => Number.isInteger(id) && id > 0);
      return selected;
    }
    return empresaIds.length ? empresaIds : userEmpresaIds;
  }, [empresaIds, userEmpresaIds, tempFilterData.EMPRESA_IDS]);

  const handleStatusFilterClick = useCallback((status) => {
    const newVal = tempFilterData.ESTATUS === status ? '' : status;
    setTempFilterData(prev => {
      const updated = { ...prev, ESTATUS: newVal };
      setFilterData(updated);
      setTableParams(prev => ({ ...prev, page: 1 }));
      return updated;
    });
  }, [tempFilterData.ESTATUS]);

  const applyFilters = useCallback((nextFilters = tempFilterData, nextQuickSearch = quickSearch) => {
    const cleanSearch = String(nextQuickSearch || '').trim();
    const updatedParams = {
      ...tableParamsRef.current,
      globalQuery: cleanSearch,
      page: 1,
    };
    tableParamsRef.current = updatedParams;
    setLoadingData(true);
    setFilterData(nextFilters);
    setTableParams(updatedParams);
  }, [quickSearch, tempFilterData]);

  const updateVisibleFilter = useCallback((field, value, options = {}) => {
    const { apply = false } = options;
    setTempFilterData(prev => {
      const updated = { ...prev, [field]: value };
      if (apply) {
        setLoadingData(true);
        setFilterData(updated);
        setTableParams(current => {
          const next = { ...current, page: 1 };
          tableParamsRef.current = next;
          return next;
        });
      }
      return updated;
    });
  }, []);

  useEffect(() => {
    const handleEmpresaChange = () => {
      setTempFilterData(prev => {
        const reseted = { ...prev, EMPRESA_IDS: [], EMPRESA: '' };
        setFilterData(reseted);
        return reseted;
      });
      setTableParams(prev => ({ ...prev, page: 1 }));
    };

    window.addEventListener('empresaCambiada', handleEmpresaChange);
    return () => window.removeEventListener('empresaCambiada', handleEmpresaChange);
  }, []);

  useEffect(() => {
    const cargarEmpresas = async () => {
      if (!apiUrl || !usuarioId) return;

      try {
        const response = await axios.get(`${apiUrl}/solicitudesAdmin/empresas/${usuarioId}`);
        const empresas = response.data?.empresas || [];
        const mapped = empresas
          .map(empresa => ({
            id: Number(empresa.EMPRESAID),
            nombre: empresa.NOMBRE_EMPRESA,
          }));
        setEmpresaOptions(mapped);

        if (mapped.length === 1) {
          const yaTieneEmpresa = (filterData.EMPRESA_IDS || []).includes(mapped[0].id);
          if (!yaTieneEmpresa) {
            const updated = { ...tempFilterData, EMPRESA_IDS: [mapped[0].id], EMPRESA: mapped[0].nombre };
            setTempFilterData(prev => ({ ...prev, EMPRESA_IDS: [mapped[0].id], EMPRESA: mapped[0].nombre }));
            setFilterData(prev => ({ ...prev, EMPRESA_IDS: [mapped[0].id], EMPRESA: mapped[0].nombre }));
          }
        }
      } catch (err) {
        console.error('Error cargando empresas para historial:', err);
        const fallback = empresaIds.length === 1
          ? [{ id: empresaIds[0], nombre: nombreEmpresa || `Empresa ${empresaIds[0]}` }]
          : [];
        setEmpresaOptions(fallback);
      }
    };

    cargarEmpresas();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [apiUrl, usuarioId]);

  useEffect(() => {
    const cargarOpcionesPorFecha = async () => {
      if (!apiUrl || !selectedCatalogEmpresaIds.length) {
        setFilterOptionRows([]);
        return;
      }

      try {
        const response = await axios.post(`${apiUrl}/solicitudesAdmin/opcionesFiltros`, {
          idEmpresa: selectedCatalogEmpresaIds,
          usuarioId,
          filterData: {},
        });
        const rows = response.data?.rows || [];
        if (rows.length) {
          setFilterOptionRows(rows);
          return;
        }

        const departamentos = response.data?.departamentos || [];
        const centrosCosto = response.data?.centrosCosto || [];
        const proveedores = response.data?.proveedores || [];
        const divisas = response.data?.divisas || [];
        setFilterOptionRows([
          ...departamentos.map(DEPARTAMENTO => ({ DEPARTAMENTO })),
          ...centrosCosto.map(CENTROCOSTO => ({ CENTROCOSTO })),
          ...proveedores.map(PROVEEDOR => ({ PROVEEDOR })),
          ...divisas.map(DIVISA => ({ DIVISA })),
        ]);
      } catch (err) {
        console.error('Error cargando opciones de filtros por fecha:', err);
        setFilterOptionRows([]);
      }
    };

    cargarOpcionesPorFecha();
  }, [apiUrl, selectedCatalogEmpresaIds, usuarioId]);

  const handleTableStateChange = useCallback((newState) => {
    // En SolicitudesAdmin la búsqueda global se controla por quickSearch y el botón Buscar.
    // Solo actualizar globalQuery si la tabla envió explícitamente un texto de búsqueda no vacío;
    // de lo contrario conservar siempre la búsqueda actual.
    const currentGQ = tableParamsRef.current.globalQuery || '';
    const newGQ = (newState.globalQuery !== undefined && String(newState.globalQuery).trim() !== '')
      ? String(newState.globalQuery).trim()
      : currentGQ;
    const newSF = newState.sortConfig?.field || 'FECHASOLICITUD';
    const newSD = newState.sortConfig?.direction || 'DESC';
    const newPage = (newState.page ?? 0) + 1;
    const newPageSize = newState.rowsPerPage || 25;

    setTableParams(prev => {
      const resetPage = prev.globalQuery !== newGQ ||
                        prev.sortField !== newSF ||
                        prev.sortDir !== newSD ||
                        prev.pageSize !== newPageSize;

      const nextRow = {
        page: resetPage ? 1 : newPage,
        pageSize: newPageSize,
        globalQuery: newGQ,
        sortField: newSF,
        sortDir: newSD,
        hiddenCols: newState.hiddenCols || []
      };

      if (JSON.stringify(prev) === JSON.stringify(nextRow)) return prev;
      tableParamsRef.current = nextRow;
      return nextRow;
    });
  }, []);

  const statusChipsMemo = useMemo(() => ([
    { label: 'Sin sol.',       value: 'Sin solicitud',     bg: '#f8fafc', color: '#64748b', help: 'La orden no tiene solicitud registrada' },
    { label: 'Solicitud',      value: 'Tiene solicitud',   bg: '#dcfce7', color: '#16a34a', help: 'Solicitudes en firmas o autorizadas' },
    { label: 'Solicitar pago', value: 'Solicitar pago',    bg: '#eff6ff', color: '#1e40af', help: 'Pago solicitado en reporte' },
    { label: 'Pagada',         value: 'Pagada',            bg: '#eaf7ea', color: '#168a3a', help: 'La orden está pagada completamente' },
    { label: 'Comprobante',    value: 'Comprobante',       bg: '#fae8ff', color: '#c026d3', help: 'Comprobante de pago cargado' },
    { label: 'Entrada',        value: 'Entrada',           bg: '#ecfeff', color: '#0891b2', help: 'Entrada de mercancía registrada' },
  ]), []);

  const optionSourceRows = useMemo(() => (
    filterOptionRows.length ? filterOptionRows : (Array.isArray(data?.contenido) ? data.contenido : [])
  ), [data?.contenido, filterOptionRows]);

  const getOptionRows = useCallback((excludeField = '') => {
    const selectedDepartamentos = tempFilterData.DEPARTAMENTOS || [];
    const selectedCentros = getCentroCostoFilterValues(tempFilterData.CENTRO_COSTOS || []);
    const selectedProveedores = tempFilterData.PROVEEDORES || [];
    const selectedDivisas = tempFilterData.DIVISAS || [];

    return optionSourceRows.filter(row => {
      const centroNombre = getCentroCostoFilterValue(buildCentroCostoOption(row));

      return (
        (excludeField === 'DEPARTAMENTOS' || matchesAnyOption(row.DEPARTAMENTO, selectedDepartamentos)) &&
        (excludeField === 'CENTRO_COSTOS' || matchesAnyOption(centroNombre, selectedCentros)) &&
        (excludeField === 'PROVEEDORES' || matchesAnyOption(row.PROVEEDOR, selectedProveedores)) &&
        (excludeField === 'DIVISAS' || matchesAnyOption(row.DIVISA, selectedDivisas))
      );
    });
  }, [optionSourceRows, tempFilterData]);

  // Cargar Catálogos de Proyectos, Divisiones y Centros de Costo (acotados a los permisos del usuario)
  useEffect(() => {
    if (!apiUrl || !selectedCatalogEmpresaIds.length) return;

    axios.post(`${apiUrl}/proyectosTabla`, { idEmpresa: selectedCatalogEmpresaIds, usuarioId, idPerfil })
      .then(res => {
        const raw = res.data?.contenido || (Array.isArray(res.data) ? res.data : []);
        setProyectosCatalogo(raw.filter(p => Boolean(p.ACTIVO)));
      })
      .catch(() => setProyectosCatalogo([]));

    axios.post(`${apiUrl}/divisionesTabla`, { idEmpresa: selectedCatalogEmpresaIds, usuarioId, idPerfil })
      .then(res => {
        const raw = res.data?.contenido || (Array.isArray(res.data) ? res.data : []);
        setDivisionesCatalogo(raw.filter(d => Boolean(d.ACTIVO)));
      })
      .catch(() => setDivisionesCatalogo([]));

    axios.post(`${apiUrl}/centroCostosTabla`, { idEmpresa: selectedCatalogEmpresaIds, usuarioId, idPerfil })
      .then(res => {
        const raw = res.data?.contenido || (Array.isArray(res.data) ? res.data : []);
        setCentrosCostoCatalogo(raw.filter(c => Boolean(c.ACTIVO)));
      })
      .catch(() => setCentrosCostoCatalogo([]));
  }, [apiUrl, selectedCatalogEmpresaIds, usuarioId, idPerfil]);

  // ─── Opciones Dinámicas en Cascada Jerárquica ───
  const proyectoOptions = useMemo(() => {
    const allowedProyIds = new Set(
      centrosCostoCatalogo.map(c => Number(c.ID_PROYECTO)).filter(id => id > 0)
    );

    const map = new Map();
    proyectosCatalogo.forEach(p => {
      const pid = Number(p.ID_PROYECTO);
      if (centrosCostoCatalogo.length === 0 || allowedProyIds.has(pid)) {
        if (p.ID_PROYECTO && p.NOMBRE_PROYECTO) {
          map.set(pid, p.NOMBRE_PROYECTO);
        }
      }
    });
    const list = Array.from(map.entries()).map(([id, nombre]) => ({ id, nombre }));
    list.sort((a, b) => a.nombre.localeCompare(b.nombre, 'es'));
    return list;
  }, [proyectosCatalogo, centrosCostoCatalogo]);

  const divisionOptions = useMemo(() => {
    const selectedProyIds = (tempFilterData.PROYECTOS || []).map(Number);
    const allowedDivIds = new Set(
      centrosCostoCatalogo.map(c => Number(c.ID_DIVISION)).filter(id => id > 0)
    );

    const map = new Map();
    divisionesCatalogo.forEach(d => {
      const pId = Number(d.ID_PROYECTO);
      const dId = Number(d.ID_DIVISION);
      
      const matchProy = !selectedProyIds.length || selectedProyIds.includes(pId);
      const matchAllowed = centrosCostoCatalogo.length === 0 || allowedDivIds.has(dId);

      if (matchProy && matchAllowed) {
        if (d.ID_DIVISION && d.NOMBRE_DIVISION) {
          map.set(dId, d.NOMBRE_DIVISION);
        }
      }
    });
    const list = Array.from(map.entries()).map(([id, nombre]) => ({ id, nombre }));
    list.sort((a, b) => a.nombre.localeCompare(b.nombre, 'es'));
    return list;
  }, [divisionesCatalogo, tempFilterData.PROYECTOS, centrosCostoCatalogo]);

  const centroCostoOptions = useMemo(() => {
    const selectedProyIds = (tempFilterData.PROYECTOS || []).map(Number);
    const selectedDivIds = (tempFilterData.DIVISIONES || []).map(Number);

    let filtered = centrosCostoCatalogo;
    if (selectedProyIds.length > 0) {
      filtered = filtered.filter(c => selectedProyIds.includes(Number(c.ID_PROYECTO)));
    }
    if (selectedDivIds.length > 0) {
      filtered = filtered.filter(c => selectedDivIds.includes(Number(c.ID_DIVISION)));
    }

    const ccList = filtered.map(c => {
      const num = String(c.NUMERO_CC || c.NUMERO || '').trim();
      const nom = String(c.CENTRO_COSTO || c.NOMBRE || '').trim();
      if (num && nom && num.toUpperCase() !== nom.toUpperCase()) return `${num} - ${nom}`;
      return num || nom;
    }).filter(Boolean);

    if (ccList.length > 0) {
      return Array.from(new Set(ccList)).sort((a, b) => a.localeCompare(b, 'es'));
    }

    return getUniqueOptions(getOptionRows('CENTRO_COSTOS').map(buildCentroCostoOption));
  }, [centrosCostoCatalogo, tempFilterData.PROYECTOS, tempFilterData.DIVISIONES, getOptionRows]);

  const hasHierarchicalFilter = useMemo(() => {
    return Boolean(
      (tempFilterData.EMPRESA_IDS || []).length ||
      (tempFilterData.PROYECTOS || []).length ||
      (tempFilterData.DIVISIONES || []).length ||
      (tempFilterData.CENTRO_COSTOS || []).length
    );
  }, [tempFilterData.EMPRESA_IDS, tempFilterData.PROYECTOS, tempFilterData.DIVISIONES, tempFilterData.CENTRO_COSTOS]);

  const hierarchicalSummary = useMemo(() => {
    const parts = [];
    const empCount = (tempFilterData.EMPRESA_IDS || []).length;
    const proyCount = (tempFilterData.PROYECTOS || []).length;
    const divCount = (tempFilterData.DIVISIONES || []).length;
    const ccCount = (tempFilterData.CENTRO_COSTOS || []).length;

    if (empCount > 0) {
      if (empCount === 1) {
        const found = empresaOptions.find(e => e.id === tempFilterData.EMPRESA_IDS[0]);
        parts.push(found ? found.nombre : '1 Empresa');
      } else {
        parts.push(`${empCount} Emp`);
      }
    }
    if (proyCount > 0) {
      if (proyCount === 1) {
        const found = proyectoOptions.find(p => p.id === tempFilterData.PROYECTOS[0]);
        parts.push(found ? found.nombre : '1 Proy');
      } else {
        parts.push(`${proyCount} Proy`);
      }
    }
    if (divCount > 0) {
      if (divCount === 1) {
        const found = divisionOptions.find(d => d.id === tempFilterData.DIVISIONES[0]);
        parts.push(found ? found.nombre : '1 Grupo');
      } else {
        parts.push(`${divCount} Grupos`);
      }
    }
    if (ccCount > 0) {
      if (ccCount === 1) {
        parts.push(tempFilterData.CENTRO_COSTOS[0]);
      } else {
        parts.push(`${ccCount} CC`);
      }
    }

    if (!parts.length) return 'Empresa / Proyecto / Grupo / CC';
    return parts.join(' / ');
  }, [tempFilterData.EMPRESA_IDS, tempFilterData.PROYECTOS, tempFilterData.DIVISIONES, tempFilterData.CENTRO_COSTOS, empresaOptions, proyectoOptions, divisionOptions]);

  const departamentoOptions = useMemo(() => (
    getUniqueOptions(getOptionRows('DEPARTAMENTOS').map(row => row.DEPARTAMENTO))
  ), [getOptionRows]);

  const proveedorOptions = useMemo(() => (
    getUniqueOptions(getOptionRows('PROVEEDORES').map(row => row.PROVEEDOR))
  ), [getOptionRows]);

  const divisaOptions = useMemo(() => (
    getUniqueOptions(getOptionRows('DIVISAS').map(row => row.DIVISA))
  ), [getOptionRows]);

  const extraFiltersCount = useMemo(() => {
    return ((tempFilterData.DEPARTAMENTOS || []).length) +
           ((tempFilterData.PROVEEDORES || []).length) +
           ((tempFilterData.DIVISAS || []).length);
  }, [tempFilterData.DEPARTAMENTOS, tempFilterData.PROVEEDORES, tempFilterData.DIVISAS]);

  const hasAnyActiveFilter = useMemo(() => {
    return hasHierarchicalFilter ||
      extraFiltersCount > 0 ||
      Boolean(tempFilterData.ESTATUS) ||
      Boolean(quickSearch) ||
      tempFilterData.FECHA_INICIO !== baseFilters.FECHA_INICIO ||
      tempFilterData.FECHA_FIN !== baseFilters.FECHA_FIN;
  }, [hasHierarchicalFilter, extraFiltersCount, tempFilterData.ESTATUS, quickSearch, tempFilterData.FECHA_INICIO, tempFilterData.FECHA_FIN, baseFilters]);

  const resetFilters = useCallback(() => {
    setTempFilterData(baseFilters);
    setFilterData(baseFilters);
    setQuickSearch('');
    applyFilters(baseFilters, '');
  }, [baseFilters, applyFilters]);

  const abortRef = useRef(null);

  const fetchData = useCallback(async () => {
    if (!apiUrl || !loggedInUsername || !selectedEmpresaIds.length || !idPerfil) return;

    if (abortRef.current) abortRef.current.abort();
    const controller = new AbortController();
    abortRef.current = controller;

    setLoadingData(true);
    try {
      const { page, pageSize, globalQuery, sortField, sortDir } = tableParams;

      const shouldIgnoreDates = Boolean(String(globalQuery || '').trim());

      // ── Resolver centros de costo según Jerarquía (Proyecto / División / CC) ──
      const selectedProyIds = (filterData.PROYECTOS || []).map(Number);
      const selectedDivIds = (filterData.DIVISIONES || []).map(Number);
      const directCcValues = (filterData.CENTRO_COSTOS || []).length
        ? getCentroCostoFilterValues(filterData.CENTRO_COSTOS)
        : [];

      let hierarchyCcValues = [];
      if (directCcValues.length > 0) {
        hierarchyCcValues = directCcValues;
      } else if (selectedDivIds.length > 0 || selectedProyIds.length > 0) {
        const ccs = centrosCostoCatalogo.filter(c => {
          const matchDiv = !selectedDivIds.length || selectedDivIds.includes(Number(c.ID_DIVISION));
          const matchProy = !selectedProyIds.length || selectedProyIds.includes(Number(c.ID_PROYECTO));
          const matchEmp = !selectedEmpresaIds.length || !c.IDEMPRESA || selectedEmpresaIds.includes(Number(c.IDEMPRESA));
          return matchDiv && matchProy && matchEmp;
        });
        hierarchyCcValues = Array.from(new Set(ccs.flatMap(c => [
          String(c.NUMERO_CC || c.NUMERO || '').trim(),
          String(c.CENTRO_COSTO || c.NOMBRE || '').trim()
        ]).filter(Boolean)));
        if (!hierarchyCcValues.length) hierarchyCcValues = ['__NO_MATCH__'];
      }

      const serverFilterData = {
        ...filterData,
        FECHA_INICIO: shouldIgnoreDates ? '' : filterData.FECHA_INICIO,
        FECHA_FIN: shouldIgnoreDates ? '' : filterData.FECHA_FIN,
        PROVEEDOR: (filterData.PROVEEDORES || []).length ? filterData.PROVEEDORES : filterData.PROVEEDOR,
        CENTRO_COSTO: hierarchyCcValues.length
          ? hierarchyCcValues
          : filterData.CENTRO_COSTO,
        DEPARTAMENTO: (filterData.DEPARTAMENTOS || []).length ? filterData.DEPARTAMENTOS : filterData.DEPARTAMENTO,
        DIVISA: (filterData.DIVISAS || []).length ? filterData.DIVISAS : filterData.DIVISA,
      };

      delete serverFilterData.EMPRESA_IDS;
      delete serverFilterData.PROYECTOS;
      delete serverFilterData.PROYECTO;
      delete serverFilterData.DIVISIONES;
      delete serverFilterData.DIVISION;
      delete serverFilterData.PROVEEDORES;
      delete serverFilterData.DIVISAS;
      delete serverFilterData.CENTRO_COSTOS;
      delete serverFilterData.DEPARTAMENTOS;

      const combinedFilter = {
        ...buildFilterPayload(serverFilterData),
        globalQuery: globalQuery || '',
        sortField: sortField || 'FECHASOLICITUD',
        sortDir: sortDir || 'DESC',
        page,
        pageSize
      };

      const response = await axios.post(
        `${apiUrl}/solicitudesAdminTabla1`,
        {
          idEmpresa: selectedEmpresaIds,
          idPerfil,
          nombreEmpresa,
          loggedInUsername,
          usuarioId,
          filterData: combinedFilter,
        },
        { signal: controller.signal, headers: { 'Cache-Control': 'no-cache, no-store, must-revalidate', 'Pragma': 'no-cache', 'Expires': '0' } }
      );

      setData({ ...response.data, contenido: sortByRecentDate(response.data?.contenido || [], combinedFilter.EVALUAR_POR_FECHA) });
      setError(null);
    } catch (err) {
      if (err?.name === 'CanceledError' || err?.code === 'ERR_CANCELED') return;
      console.error('Error al cargar los datos:', err);
      setData({ contenido: [] });
      setError('No se pudieron cargar los datos. Intenta más tarde.');
      handleOpenSweetAlert('No se pudieron cargar los datos.', 'error');
    } finally {
      if (abortRef.current === controller) {
        setLoadingData(false);
        abortRef.current = null;
      }
    }
  }, [apiUrl, loggedInUsername, usuarioId, selectedEmpresaIds, idPerfil, nombreEmpresa, filterData, tableParams]);

  useEffect(() => {
    const timer = setTimeout(() => {
      fetchData();
    }, 80);
    return () => {
      clearTimeout(timer);
      if (abortRef.current) abortRef.current.abort();
    };
  }, [fetchData]);

  const fetchSolicitudSummary = useCallback(async (row, solicitudIdOverride = null) => {
    const selectedSolicitudId = Number(solicitudIdOverride || row?.ID_SOLICITUD || 0);
    // IMPORTANTE: NO hacer { ...row } porque los campos originales (FOLIO_OC, PROVEEDOR, etc.)
    // son propiedades no-enumerables en la fila compacta. El spread las perdería.
    // Usamos siempre la referencia original de row.

    setStatusDetail({ open: true, type: 'solicitud', row, loading: true, data: null });
    setLoadingSolicitudesRelacionadas(true);

    const basePayload = {
      idDatosErp: row?.IDDATOSERP,
      empresaId: row?.EMPRESAID,
      folioOc: row?.FOLIO_OC || row?.FOLIO,
      autsolpagoid: selectedSolicitudId || row?.ID_SOLICITUD,
    };

    const readSettled = (result, fallback = {}) => (
      result.status === 'fulfilled' ? (result.value?.data || fallback) : fallback
    );

    try {
      const [
        solicitudRes,
        reporteRes,
        pagoRes,
        entradaRes,
        comprobanteRes,
        relacionadasRes,
      ] = await Promise.allSettled([
        axios.post(`${apiUrl}/solicitudesAdmin/detalleEstatus`, { tipo: 'solicitud', ...basePayload }),
        axios.post(`${apiUrl}/solicitudesAdmin/detalleEstatus`, { tipo: 'reporte', ...basePayload }),
        axios.post(`${apiUrl}/solicitudesAdmin/detalleEstatus`, { tipo: 'pago', ...basePayload }),
        axios.post(`${apiUrl}/solicitudesAdmin/detalleEstatus`, { tipo: 'entrada', ...basePayload }),
        axios.post(`${apiUrl}/solicitudesAdmin/detalleEstatus`, { tipo: 'comprobante', ...basePayload }),
        axios.post(`${apiUrl}/solicitudesAdmin/solicitudesRelacionadas`, {
          empresaId: row?.EMPRESAID,
          folioOc: row?.FOLIO_OC || row?.FOLIO,
          fechaInicio: filterData.FECHA_INICIO,
          fechaFin: filterData.FECHA_FIN,
        }),
      ]);

      const relatedData = readSettled(relacionadasRes, { solicitudes: [], total: 0, dentroRango: 0, fueraRango: 0 });
      setSolicitudesRelacionadas(relatedData);
      setStatusDetail({
        open: true,
        type: 'solicitud',
        row,
        loading: false,
        data: {
          selectedSolicitudId,
          solicitud: readSettled(solicitudRes),
          reporte: readSettled(reporteRes),
          pago: readSettled(pagoRes),
          entrada: readSettled(entradaRes),
          comprobante: readSettled(comprobanteRes),
          relacionadas: relatedData,
        },
      });
    } catch (err) {
      console.error('Error al consultar resumen de solicitud:', err);
      setStatusDetail({
        open: true,
        type: 'solicitud',
        row,
        loading: false,
        data: { ok: false, error: 'No se pudo consultar el resumen.' },
      });
      handleOpenSweetAlert('No se pudo consultar el resumen de la solicitud.', 'error');
    } finally {
      setLoadingSolicitudesRelacionadas(false);
    }
  }, [apiUrl, filterData.FECHA_FIN, filterData.FECHA_INICIO]);



  const handleAdminStatusClick = useCallback(async (type, row) => {
    setStatusDetail({ open: true, type, row, loading: type !== 'solicitud', data: null });

    if (type === 'solicitud') {
      fetchSolicitudSummary(row);
      return;
    }

    try {
      const payload = {
        tipo: type,
        idDatosErp: row?.IDDATOSERP,
        empresaId: row?.EMPRESAID || row?.IDEMPRESA,
        folioOc: row?.FOLIO_OC || row?.FOLIO,
        // Para comprobante, NO enviamos autsolpagoid porque row.ID puede ser IDDATOSERP (campo del ERP)
        // El servidor resuelve todos los AUTSOLPAGOID a partir de folioOc+empresaId
        autsolpagoid: type === 'comprobante' ? 0 : (row?.ID_SOLICITUD || row?.ID),
      };

      const response = await axios.post(`${apiUrl}/solicitudesAdmin/detalleEstatus`, payload);

      setStatusDetail({ open: true, type, row, loading: false, data: response.data || null });
    } catch (err) {
      console.error('Error al consultar detalle de estatus:', err);
      setStatusDetail({ open: true, type, row, loading: false, data: { ok: false, error: 'No se pudo consultar el detalle.' } });
      handleOpenSweetAlert('No se pudo consultar el detalle del estatus.', 'error');
    }
  }, [apiUrl, fetchSolicitudSummary]);





  const dataForExport = useMemo(() => {
    const contenido = Array.isArray(data?.contenido) ? data.contenido : [];

    return {
      ...data,
      contenido: contenido.map((row) => buildAdminExportRow(row)),
    };
  }, [data]);

  // ✅ Dinámico solo para tabla (compact rows)
  const dataForTable = useMemo(() => {
    const contenido = Array.isArray(data?.contenido) ? data.contenido : [];

    return {
      ...data,
      contenido: contenido.map((row) => {
        const authColumns = buildAdminAuthColumns(row);
        const compactRow = {
          ...row,
          ...authColumns,
          OC_REQUI: '',
          FECHA_SOLICITANTE: '',
          PROVEEDOR_COMPRADOR: '',
          CENTRO_DEPTO: '',
          TOTAL_DIVISA: '',
          MONTO_RESTANTE: '',
          EXHIBIT: row?.EXHIBIT || row?.EXHIBICION || '',
          FACTURA_COMPACTA: '',
          SOLICITUD_RESUMEN: '',
          REPORTE_SOLICITADO_RESUMEN: '',
          ENTRADA_RESUMEN: '',
          PAGO_ERP_RESUMEN: '',
          PAGO_RESUMEN: '',
          __hideCompactId: true,
          __disableStatusRowColor: true,
        };

        if (row?.partidas) {
          compactRow.partidas = row.partidas;
        }

        return compactRow;
      }),
    };
  }, [data]);

  const exportLabelsExtended = useMemo(() => {
    const contenido = Array.isArray(dataForExport?.contenido) ? dataForExport.contenido : [];

    let maxAut = 0;
    let maxTeso = 0;

    for (const r of contenido) {
      for (const k of Object.keys(r || {})) {
        const m1 = k.match(/^FECHA_AUT_N(\d+)$/);
        if (m1) maxAut = Math.max(maxAut, Number(m1[1]));

        const m2 = k.match(/^FECHA_TESO_N(\d+)$/);
        if (m2) maxTeso = Math.max(maxTeso, Number(m2[1]));
      }
    }

    const hidden = (hiddenCols || []).filter((key) => !compactTableHiddenFields.includes(key));
    const labels = {
      ...exportLabels,
      FECHA_AUT_ULTIMA: 'Última fecha autorización',
    };
    delete labels.EXHIBIT;

    for (let i = 1; i <= maxAut; i++) {
      labels[`FECHA_AUT_N${i}`] = `Fecha autorización N${i}`;
    }

    for (let i = 1; i <= maxTeso; i++) {
      labels[`FECHA_TESO_N${i}`] = `Fecha tesorería N${i}`;
    }

    labels.EXHIBIT = 'EXHIBIT';

    // Filtrar columnas ocultas dinámicamente
    const finalLabels = {};
    const hiddenSet = new Set(hidden);
    
    Object.keys(labels).forEach(key => {
      if (!hiddenSet.has(key)) {
          finalLabels[key] = labels[key];
      }
    });

    return finalLabels;
  }, [dataForExport, hiddenCols, compactTableHiddenFields]);

  const customExportOptions = useMemo(() => ([
    {
      key: 'desglosado',
      label: 'Reporte por Solicitud (Desglosado)',
      description: 'Muestra cada solicitud en su propia fila con su monto, exhibit, observaciones, estatus y facturas.',
    },
    {
      key: 'summary',
      label: 'Reporte por Orden de Compra (Resumen)',
      description: 'Consolida 1 fila por cada Orden de Compra con totales generales.',
    },
  ]), []);

  const handleExportFetchAll = useCallback(async ({ scope = 'filters', fromDate = '', toDate = '', variant = 'desglosado' } = {}) => {
    if (!apiUrl || !loggedInUsername || !selectedEmpresaIds.length || !idPerfil) return dataForExport;

    // Construir el filterData según el scope elegido por el usuario
    let exportFilterData;

    if (scope === 'all') {
      // Sin filtros: solo empresa, sin fechas, sin departamento, sin proveedor, etc.
      exportFilterData = {};
    } else if (scope === 'dateRange') {
      // Solo el rango de fechas que escribió el usuario en el diálogo, sin otros filtros
      exportFilterData = {
        FECHA_INICIO: fromDate || null,
        FECHA_FIN: toDate || null,
      };
    } else {
      // 'filters': usar todos los filtros actuales igual que la tabla
      const currentGQ = tableParams.globalQuery || '';
      const shouldIgnoreDates = Boolean(String(currentGQ).trim());
      const serverFilterData = buildVisibleServerFilters(filterData, {
        ignoreDateFilters: shouldIgnoreDates,
      });
      exportFilterData = {
        ...buildFilterPayload(serverFilterData),
        globalQuery: currentGQ,
      };
    }

    try {
      const sortRowsByFecha = (rows) => sortByRecentDate(rows, exportFilterData.EVALUAR_POR_FECHA);

      // ── MODO DESGLOSADO: 1 fila por solicitud individual con su monto, exhibit, observaciones, facturas ──
      if (variant === 'desglosado' || !variant || variant === 'default') {
        const resp = await axios.post(
          `${apiUrl}/solicitudesAdmin/exportarDesglosado`,
          {
            idEmpresa: selectedEmpresaIds,
            idPerfil,
            nombreEmpresa,
            loggedInUsername,
            usuarioId,
            filterData: exportFilterData,
          }
        );
        const contenidoDesglosado = Array.isArray(resp.data?.contenido) ? resp.data.contenido : [];
        return {
          total: contenidoDesglosado.length,
          hasMore: false,
          contenido: sortRowsByFecha(contenidoDesglosado),
        };
      }

      // ── MODO RESUMEN: 1 fila por OC consolidada ──
      const batchSize = 500;
      const fetchPage = (page) => axios.post(
        `${apiUrl}/solicitudesAdminTabla1`,
        {
          idEmpresa: selectedEmpresaIds,
          idPerfil,
          nombreEmpresa,
          loggedInUsername,
          filterData: {
            ...exportFilterData,
            sortField: 'FECHASOLICITUD',
            sortDir: 'DESC',
            page,
            pageSize: batchSize,
          },
        }
      );

      // ── 1. Página 1: obtener datos + total real ──
      const firstResp = await fetchPage(1);
      const firstContent = Array.isArray(firstResp.data?.contenido) ? firstResp.data.contenido : [];
      const totalFromServer = firstResp.data?.total || 0;

      // Si todo cabe en la primera página, terminamos ya
      if (firstContent.length < batchSize) {
        return {
          total: firstContent.length,
          hasMore: false,
          contenido: sortRowsByFecha(firstContent).map((row) => buildAdminExportRow(row)),
        };
      }

      // ── 2. Si el servidor nos dio un TOTAL real mayor al batch, hacemos peticiones en PARALELO ──
      if (totalFromServer > batchSize) {
        const totalPages = Math.min(Math.ceil(totalFromServer / batchSize), 40);
        const remainingPages = Array.from({ length: totalPages - 1 }, (_, i) => i + 2);

        const restResponses = await Promise.all(remainingPages.map(fetchPage));
        const allData = [
          ...firstContent,
          ...restResponses.flatMap(r => Array.isArray(r.data?.contenido) ? r.data.contenido : []),
        ];

        return {
          total: allData.length,
          hasMore: false,
          contenido: sortRowsByFecha(allData).map((row) => buildAdminExportRow(row)),
        };
      }

      // ── 3. Si el servidor NO dio el TOTAL (pero el batch llegó lleno), hacemos peticiones SECUENCIALES ──
      let allData = [...firstContent];
      let currentPage = 2;
      let hasMore = true;

      while (hasMore) {
        const nextResp = await fetchPage(currentPage);
        const nextContent = Array.isArray(nextResp.data?.contenido) ? nextResp.data.contenido : [];
        if (nextContent.length > 0) {
          allData = [...allData, ...nextContent];
          hasMore = nextContent.length === batchSize;
          currentPage++;
        } else {
          hasMore = false;
        }
        if (currentPage > 40) break;
      }

      return {
        total: allData.length,
        hasMore: false,
        contenido: sortRowsByFecha(allData).map((row) => buildAdminExportRow(row)),
      };
    } catch (err) {
      console.error('Error fetching all data for export:', err);
      return dataForExport;
    }
  }, [apiUrl, loggedInUsername, selectedEmpresaIds, idPerfil, nombreEmpresa, filterData, usuarioId, tableParams.globalQuery, dataForExport]);



  const cancelarSolicitud = useCallback(async (correctionData) => {
    setModalStates((prev) => ({
      ...prev,
      openCancelacionModal: false,
    }));

    setLoadingAction(true);

    try {
      await axios.post(`${apiUrl}/solicitudesAdmin/cancelar`, {
        ID: modalStates.cancelarID,
        EMPRESA: modalStates.cancelarEmpresa,
        OBSERVACIONES: correctionData.detalle,
        BORRAR: 0,
        USUARIO: loggedInUsername,
        MOTIVO: correctionData.motivo,
        DETALLE: correctionData.detalle,
        SECCIONES: correctionData.secciones,
        RECLASIFICAR: correctionData.reclasificar
      });

      await fetchData();

      handleOpenSweetAlert('Solicitud rechazada correctamente.', 'success');
    } catch (error) {
      handleOpenSweetAlert('Error al cancelar la solicitud.', 'error');
    } finally {
      setLoadingAction(false);
    }
  }, [modalStates, loggedInUsername, fetchData, apiUrl]);

  const handleUploadAdjuntos = useCallback(async () => {
    if (!selectedRow || adjuntos.length === 0) return;

    const formData = new FormData();
    formData.append('ID_SOLICITUD', selectedRow.ID);
    formData.append('FOLIO', selectedRow.FOLIO || selectedRow.FOLIO_OC);
    formData.append('NOMBRE_EMPRESA', nombreEmpresa);
    formData.append('USUARIO', loggedInUsername);
    formData.append('ES_REEMPLAZO', selectedRow.ESTATUS === 'Pagada' ? '1' : '0');

    adjuntos.forEach((file) => {
      formData.append('adjunto', file);
    });

    try {
      setLoadingAction(true);
      await axios.put(`${apiUrl}/solicitudesAdmin/guardarComprobante`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });

      setFileUploadModalOpen(false);
      setAdjuntos([]);
      await fetchData();
      handleOpenSweetAlert(
        selectedRow.ESTATUS === 'Pagada'
          ? 'Comprobante de pago reemplazado correctamente.'
          : 'Comprobante de pago cargado correctamente.',
        'success'
      );
    } catch (error) {
      console.error('Error al guardar comprobante:', error);
      handleOpenSweetAlert('No se pudo guardar el comprobante.', 'error');
    } finally {
      setLoadingAction(false);
    }
  }, [adjuntos, selectedRow, nombreEmpresa, loggedInUsername, apiUrl, fetchData]);

  const renderMobileFooterMemo = useCallback(() => {
    return (
      <AdminMobileTotalsFooter
        divisas={data?.totalesPorDivisa}
        totalCount={data?.total}
      />
    );
  }, [data?.totalesPorDivisa, data?.total]);

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        minHeight: 0,
        overflow: 'hidden',
        gap: 0.8,
        p: 'clamp(6px, 0.8vw, 12px)',
        bgcolor: '#f8fafc'
      }}
    >
      {error && <div style={{ color: 'red' }}>{error}</div>}

      {/* ── BARRA SUPERIOR DE ACCESO RÁPIDO & FILTROS UNIFICADOS (Empresa / Proyecto / Grupo / CC) ── */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.2, gap: 1, width: '100%', minWidth: 0, maxWidth: '100%' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flex: 1, minWidth: 0, maxWidth: '100%' }}>

          {/* Vista Móvil Ergonomica (<md): Fila 1 (Buscador expandido + Jerárquico + Más Filtros) / Fila 2 (Fechas + Estatus + Excel + Restablecer) */}
          <Box
            sx={{
              display: { xs: 'flex', md: 'none' },
              flexDirection: 'column',
              gap: 0.8,
              width: '100%',
              minWidth: 0,
              maxWidth: '100%',
              boxSizing: 'border-box',
              bgcolor: '#ffffff',
              p: 0.8,
              borderRadius: '14px',
              border: '1px solid #e2e8f0',
              boxShadow: '0 1px 4px rgba(15, 23, 42, 0.03)',
            }}
          >
            {/* Fila 1 Móvil: Buscador expandido + Jerarquía + Filtros secundarios */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.6, width: '100%', minWidth: 0, maxWidth: '100%', boxSizing: 'border-box' }}>
              <TextField
                disabled={loadingData}
                size="small"
                placeholder="Buscar OC, folio, prov..."
                value={quickSearch}
                onChange={(e) => setQuickSearch(e.target.value)}
                onKeyUp={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    applyFilters(tempFilterData, e.currentTarget.value);
                  }
                }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon sx={{ color: '#0655A5', fontSize: 16 }} />
                    </InputAdornment>
                  ),
                  endAdornment: quickSearch ? (
                    <InputAdornment position="end">
                      <IconButton
                        disabled={loadingData}
                        size="small"
                        onClick={() => {
                          setQuickSearch('');
                          applyFilters(tempFilterData, '');
                        }}
                        sx={{ p: 0.2 }}
                      >
                        <ClearIcon sx={{ fontSize: 13 }} />
                      </IconButton>
                    </InputAdornment>
                  ) : null,
                }}
                sx={{
                  flex: 1,
                  minWidth: 0,
                  '& .MuiOutlinedInput-root': {
                    height: 34,
                    borderRadius: '10px',
                    bgcolor: '#f8fafc',
                    fontSize: '0.75rem',
                    fontWeight: 500,
                    px: 0.8,
                    '& fieldset': { borderColor: '#cbd5e1' },
                    '&:hover fieldset': { borderColor: '#94a3b8' },
                    '&.Mui-focused fieldset': { borderColor: '#0655A5', bgcolor: '#ffffff' },
                  }
                }}
              />

              {/* Botón Jerarquía */}
              <Tooltip title={`Jerarquía: ${hierarchicalSummary}`}>
                <Button
                  size="small"
                  variant={hasHierarchicalFilter ? 'contained' : 'outlined'}
                  onClick={(e) => setHierarchicalAnchorEl(e.currentTarget)}
                  sx={{
                    minWidth: 34,
                    width: 34,
                    height: 34,
                    p: 0,
                    flexShrink: 0,
                    borderRadius: '10px',
                    bgcolor: hasHierarchicalFilter ? '#eff6ff' : '#ffffff',
                    borderColor: hasHierarchicalFilter ? '#93c5fd' : '#cbd5e1',
                    color: hasHierarchicalFilter ? '#1e40af' : '#475569',
                    boxShadow: 'none',
                    '&:hover': { bgcolor: hasHierarchicalFilter ? '#dbeafe' : '#f8fafc' },
                  }}
                >
                  <AccountTreeIcon sx={{ fontSize: 17 }} />
                </Button>
              </Tooltip>

              {/* Botón Filtros Complementarios con Badge */}
              <Tooltip title="Otros filtros (Depto, Prov, Divisa)">
                <Badge
                  badgeContent={extraFiltersCount}
                  color="primary"
                  sx={{
                    flexShrink: 0,
                    '& .MuiBadge-badge': {
                      fontSize: '0.62rem',
                      height: 16,
                      minWidth: 16,
                    }
                  }}
                >
                  <Button
                    size="small"
                    variant={showFiltersPanel || extraFiltersCount > 0 ? 'contained' : 'outlined'}
                    onClick={(e) => {
                      setFilterAnchorEl(e.currentTarget);
                      setShowFiltersPanel(prev => !prev);
                    }}
                    sx={{
                      minWidth: 34,
                      width: 34,
                      height: 34,
                      p: 0,
                      flexShrink: 0,
                      borderRadius: '10px',
                      bgcolor: showFiltersPanel ? '#0f172a' : extraFiltersCount > 0 ? '#0655A5' : '#ffffff',
                      borderColor: extraFiltersCount > 0 ? '#0655A5' : '#cbd5e1',
                      color: showFiltersPanel || extraFiltersCount > 0 ? '#ffffff' : '#475569',
                      boxShadow: 'none',
                      '&:hover': { bgcolor: showFiltersPanel ? '#1e293b' : '#054785' },
                    }}
                  >
                    <FilterListIcon sx={{ fontSize: 17 }} />
                  </Button>
                </Badge>
              </Tooltip>
            </Box>

            {/* Fila 2 Móvil: Rango de fechas horizontal + Menú Estatus + Excel + Restablecer */}
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 0.6,
                width: '100%',
                minWidth: 0,
                maxWidth: '100%',
                boxSizing: 'border-box',
                overflowX: 'auto',
                scrollbarWidth: 'none',
                '&::-webkit-scrollbar': { display: 'none' },
                WebkitOverflowScrolling: 'touch',
                pb: 0.2,
              }}
            >
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 0.3,
                  bgcolor: '#f8fafc',
                  px: 0.6,
                  borderRadius: '8px',
                  border: '1px solid #cbd5e1',
                  height: 28,
                  flexShrink: 0,
                  '&:hover': { borderColor: '#94a3b8' },
                }}
              >
                <Typography sx={{ fontSize: '0.66rem', fontWeight: 700, color: '#64748b' }}>Del</Typography>
                <TextField
                  size="small"
                  type="date"
                  value={tempFilterData.FECHA_INICIO}
                  onChange={(e) => updateVisibleFilter('FECHA_INICIO', e.target.value, { apply: false })}
                  onKeyDown={(e) => { if (e.key === 'Enter') applyFilters(); }}
                  variant="standard"
                  InputProps={{ disableUnderline: true }}
                  sx={{
                    width: 110,
                    '& .MuiInputBase-input': {
                      fontSize: '0.68rem',
                      fontWeight: 600,
                      color: '#0f172a',
                      py: 0,
                    },
                  }}
                />
                <Typography sx={{ fontSize: '0.66rem', fontWeight: 700, color: '#64748b' }}>al</Typography>
                <TextField
                  size="small"
                  type="date"
                  value={tempFilterData.FECHA_FIN}
                  onChange={(e) => updateVisibleFilter('FECHA_FIN', e.target.value, { apply: false })}
                  onKeyDown={(e) => { if (e.key === 'Enter') applyFilters(); }}
                  variant="standard"
                  InputProps={{ disableUnderline: true }}
                  sx={{
                    width: 110,
                    '& .MuiInputBase-input': {
                      fontSize: '0.68rem',
                      fontWeight: 600,
                      color: '#0f172a',
                      py: 0,
                    },
                  }}
                />
              </Box>

              <TableFilterChips
                items={statusChipsMemo}
                activeFilter={tempFilterData.ESTATUS || ''}
                onFilterClick={handleStatusFilterClick}
                disabled={loadingData}
                isCompact={true}
                compactLabel="Estatus"
              />

              <Box sx={{ flexShrink: 0, display: 'flex', alignItems: 'center' }}>
                <ExportButton
                  data={dataForExport}
                  onFetchAll={handleExportFetchAll}
                  columnLabels={exportLabelsExtended}
                  hiddenFields={exportHiddenFieldsMemo}
                  fileName="Seguimiento_Solicitudes.xlsx"
                  customExportOptions={customExportOptions}
                  hidePendientes={true}
                  tableLoading={loadingData}
                  btnSx={{
                    width: 28,
                    height: 28,
                    borderRadius: '8px',
                    border: '1px solid #86efac',
                    bgcolor: '#f0fdf4',
                    color: '#16a34a',
                    boxShadow: 'none',
                    p: 0,
                  }}
                />
              </Box>

              {hasAnyActiveFilter && (
                <Button
                  size="small"
                  variant="text"
                  onClick={resetFilters}
                  startIcon={<RestartAltIcon sx={{ fontSize: 13 }} />}
                  sx={{
                    height: 28,
                    px: 0.8,
                    fontSize: '0.66rem',
                    fontWeight: 700,
                    color: '#dc2626',
                    bgcolor: '#fef2f2',
                    border: '1px solid #fecaca',
                    borderRadius: '8px',
                    textTransform: 'none',
                    flexShrink: 0,
                    whiteSpace: 'nowrap',
                    '&:hover': { bgcolor: '#fee2e2' },
                  }}
                >
                  Restablecer
                </Button>
              )}
            </Box>
          </Box>

          {/* Vista Laptop (md a xl): Fechas -> Estatus -> Filtros Jerárquicos -> Más Filtros -> Buscador */}
          <Box
            sx={{
              display: { xs: 'none', md: 'flex', xl: 'none' },
              alignItems: 'center',
              gap: 0.6,
              flex: 1,
              bgcolor: '#ffffff',
              p: 0.4,
              px: 0.8,
              borderRadius: '14px',
              border: '1px solid #e2e8f0',
              boxShadow: '0 1px 4px rgba(15, 23, 42, 0.03)',
              overflowX: 'auto',
            }}
          >
            {/* 1. Rango de Fechas Integrado Estilo Cápsula */}
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 0.4,
                bgcolor: '#ffffff',
                px: 0.7,
                borderRadius: '10px',
                border: '1px solid #cbd5e1',
                height: 26,
                flexShrink: 0,
                '&:hover': { borderColor: '#94a3b8' },
              }}
            >
              <Typography sx={{ fontSize: '0.64rem', fontWeight: 700, color: '#64748b' }}>Del</Typography>
              <TextField
                size="small"
                type="date"
                value={tempFilterData.FECHA_INICIO}
                onChange={(e) => updateVisibleFilter('FECHA_INICIO', e.target.value, { apply: false })}
                onKeyDown={(e) => { if (e.key === 'Enter') applyFilters(); }}
                variant="standard"
                InputProps={{ disableUnderline: true }}
                sx={{
                  width: 110,
                  '& .MuiInputBase-input': {
                    fontSize: '0.68rem',
                    fontWeight: 600,
                    color: '#0f172a',
                    py: 0,
                  },
                }}
              />
              <Typography sx={{ fontSize: '0.64rem', fontWeight: 700, color: '#64748b' }}>al</Typography>
              <TextField
                size="small"
                type="date"
                value={tempFilterData.FECHA_FIN}
                onChange={(e) => updateVisibleFilter('FECHA_FIN', e.target.value, { apply: false })}
                onKeyDown={(e) => { if (e.key === 'Enter') applyFilters(); }}
                variant="standard"
                InputProps={{ disableUnderline: true }}
                sx={{
                  width: 110,
                  '& .MuiInputBase-input': {
                    fontSize: '0.68rem',
                    fontWeight: 600,
                    color: '#0f172a',
                    py: 0,
                  },
                }}
              />
            </Box>

            {/* 2. Menú Desplegable de Estatus */}
            <TableFilterChips
              items={statusChipsMemo}
              activeFilter={tempFilterData.ESTATUS || ''}
              onFilterClick={handleStatusFilterClick}
              disabled={loadingData}
              isCompact={true}
            />

            {/* 3. Selector Jerárquico Estilo Breadcrumbs: Empresa / Proyecto / Grupo / CC */}
            <Button
              size="small"
              variant="outlined"
              onClick={(e) => {
                setHierarchicalAnchorEl(e.currentTarget);
              }}
              startIcon={<AccountTreeIcon sx={{ fontSize: '13px !important', color: hasHierarchicalFilter ? '#0655A5' : '#64748b' }} />}
              endIcon={<ExpandMoreIcon sx={{ fontSize: 13, transform: hierarchicalAnchorEl ? 'rotate(180deg)' : 'none', transition: 'transform 0.2s' }} />}
              sx={{
                height: 26,
                px: 1,
                maxWidth: 240,
                fontSize: '0.68rem',
                fontWeight: hasHierarchicalFilter ? 700 : 500,
                borderRadius: '10px',
                bgcolor: hasHierarchicalFilter ? '#eff6ff' : '#ffffff',
                borderColor: hasHierarchicalFilter ? '#93c5fd' : '#cbd5e1',
                color: hasHierarchicalFilter ? '#1e40af' : '#334155',
                textTransform: 'none',
                boxShadow: 'none',
                flexShrink: 0,
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                '&:hover': { bgcolor: hasHierarchicalFilter ? '#dbeafe' : '#f8fafc', borderColor: '#60a5fa' },
              }}
            >
              <Box component="span" sx={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>
                {hierarchicalSummary}
              </Box>
            </Button>

            {/* 4. Botón Más Filtros (Slim) */}
            <Button
              size="small"
              variant={showFiltersPanel ? 'contained' : 'outlined'}
              onClick={(e) => {
                setFilterAnchorEl(e.currentTarget);
                setShowFiltersPanel(prev => !prev);
              }}
              startIcon={<FilterListIcon sx={{ fontSize: '13px !important' }} />}
              endIcon={<ExpandMoreIcon sx={{ fontSize: 14, transform: showFiltersPanel ? 'rotate(180deg)' : 'none', transition: 'transform 0.2s' }} />}
              sx={{
                height: 26,
                px: 0.8,
                fontSize: '0.68rem',
                fontWeight: 600,
                borderRadius: '10px',
                bgcolor: showFiltersPanel ? '#0f172a' : '#ffffff',
                borderColor: '#cbd5e1',
                color: showFiltersPanel ? '#ffffff' : '#334155',
                textTransform: 'none',
                boxShadow: 'none',
                flexShrink: 0,
                '&:hover': { bgcolor: showFiltersPanel ? '#1e293b' : '#f8fafc', borderColor: '#94a3b8' },
              }}
            >
              Más {(((tempFilterData.DEPARTAMENTOS || []).length) + ((tempFilterData.PROVEEDORES || []).length) + ((tempFilterData.DIVISAS || []).length)) > 0 && (
                <Box
                  component="span"
                  sx={{
                    ml: 0.6,
                    px: 0.6,
                    py: 0.1,
                    bgcolor: showFiltersPanel ? '#38bdf8' : '#0655A5',
                    color: '#ffffff',
                    borderRadius: '10px',
                    fontSize: '0.60rem',
                    fontWeight: 800,
                  }}
                >
                  {((tempFilterData.DEPARTAMENTOS || []).length) + ((tempFilterData.PROVEEDORES || []).length) + ((tempFilterData.DIVISAS || []).length)}
                </Box>
              )}
            </Button>

            {/* 5. Buscador */}
            <TextField
              disabled={loadingData}
              size="small"
              placeholder="Buscar..."
              value={quickSearch}
              onChange={(e) => setQuickSearch(e.target.value)}
              onKeyUp={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  applyFilters(tempFilterData, e.currentTarget.value);
                }
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon sx={{ color: '#0655A5', fontSize: 13 }} />
                  </InputAdornment>
                ),
                endAdornment: quickSearch ? (
                  <InputAdornment position="end">
                    <IconButton
                      disabled={loadingData}
                      size="small"
                      onClick={() => {
                        setQuickSearch('');
                        applyFilters(tempFilterData, '');
                      }}
                      sx={{ p: 0.2 }}
                    >
                      <ClearIcon sx={{ fontSize: 11 }} />
                    </IconButton>
                  </InputAdornment>
                ) : null,
              }}
              sx={{
                width: { xs: 120, sm: 140, md: 150 },
                flex: 'none',
                '& .MuiOutlinedInput-root': {
                  height: 26,
                  borderRadius: '10px',
                  bgcolor: '#ffffff',
                  fontSize: '0.72rem',
                  fontWeight: 500,
                  px: 1,
                  '& fieldset': { borderColor: '#cbd5e1' },
                  '&:hover fieldset': { borderColor: '#94a3b8' },
                  '&.Mui-focused fieldset': { borderColor: '#0655A5' },
                }
              }}
            />
          </Box>

          {/* ── MODO COMPLETO ESCRITORIO (PANTALLAS GRANDES: xl+) ── */}
          <Box
            sx={{
              display: { xs: 'none', xl: 'flex' },
              alignItems: 'center',
              gap: 'clamp(4px, 0.5vw, 7px)',
              bgcolor: '#ffffff',
              p: 0.5,
              px: 0.8,
              borderRadius: '12px',
              border: '1px solid #e2e8f0',
              boxShadow: '0 1px 3px rgba(0,0,0,0.02)',
              flex: 1,
              overflowX: 'auto',
            }}
          >
            {/* 1. Fechas en cápsula integrada */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, bgcolor: '#ffffff', px: 0.9, py: 0.1, borderRadius: '8px', border: '1px solid #cbd5e1', height: 28, flexShrink: 0, boxShadow: '0 1px 2px rgba(0,0,0,0.03)', '&:hover': { borderColor: '#94a3b8' } }}>
              <Typography sx={{ fontSize: '0.66rem', fontWeight: 700, color: '#64748b' }}>Del</Typography>
              <TextField
                size="small"
                type="date"
                value={tempFilterData.FECHA_INICIO}
                onChange={(e) => updateVisibleFilter('FECHA_INICIO', e.target.value, { apply: false })}
                onKeyDown={(e) => { if (e.key === 'Enter') applyFilters(); }}
                variant="standard"
                InputProps={{ disableUnderline: true }}
                sx={{
                  width: 98,
                  '& .MuiInputBase-input': {
                    fontSize: '0.66rem',
                    fontWeight: 600,
                    color: '#0f172a',
                    py: 0,
                    px: 0,
                    '&::-webkit-calendar-picker-indicator': {
                      cursor: 'pointer',
                      opacity: 0.6,
                      fontSize: '0.82rem',
                      marginLeft: '2px',
                      filter: 'invert(0.2) sepia(0.8) saturate(4) hue-rotate(185deg)',
                      '&:hover': { opacity: 1 },
                    },
                  },
                }}
              />
              <Box sx={{ width: '1px', height: 14, bgcolor: '#cbd5e1', mx: 0.3 }} />
              <Typography sx={{ fontSize: '0.66rem', fontWeight: 700, color: '#64748b' }}>al</Typography>
              <TextField
                size="small"
                type="date"
                value={tempFilterData.FECHA_FIN}
                onChange={(e) => updateVisibleFilter('FECHA_FIN', e.target.value, { apply: false })}
                onKeyDown={(e) => { if (e.key === 'Enter') applyFilters(); }}
                variant="standard"
                InputProps={{ disableUnderline: true }}
                sx={{
                  width: 98,
                  '& .MuiInputBase-input': {
                    fontSize: '0.66rem',
                    fontWeight: 600,
                    color: '#0f172a',
                    py: 0,
                    px: 0,
                    '&::-webkit-calendar-picker-indicator': {
                      cursor: 'pointer',
                      opacity: 0.6,
                      fontSize: '0.82rem',
                      marginLeft: '2px',
                      filter: 'invert(0.2) sepia(0.8) saturate(4) hue-rotate(185deg)',
                      '&:hover': { opacity: 1 },
                    },
                  },
                }}
              />
            </Box>

            {/* 2. Select Empresa */}
            <Autocomplete
              disabled={loadingData}
              multiple
              disableCloseOnSelect
              forcePopupIcon
              size="small"
              options={empresaOptions}
              value={empresaOptions.filter(empresa => (tempFilterData.EMPRESA_IDS || []).includes(empresa.id))}
              onChange={(_, values) => {
                const updated = {
                  ...tempFilterData,
                  EMPRESA_IDS: values.map(v => v.id),
                  EMPRESA: values.map(v => v.nombre).join(','),
                  PROYECTOS: [],
                  PROYECTO: '',
                  DIVISIONES: [],
                  DIVISION: '',
                  CENTRO_COSTOS: [],
                  CENTRO_COSTO: '',
                  DEPARTAMENTOS: [],
                  PROVEEDORES: [],
                  DIVISAS: [],
                };
                setTempFilterData(updated);
                setFilterData(updated);
                setTableParams(prev => ({ ...prev, page: 1 }));
              }}
              getOptionLabel={(option) => option?.nombre || ''}
              isOptionEqualToValue={(option, value) => option?.id === value?.id}
              limitTags={1}
              renderTags={() => null}
              renderOption={renderCheckboxOption}
              renderInput={(params) => renderSummaryInput(
                params,
                'Empresa',
                empresaOptions.filter(empresa => (tempFilterData.EMPRESA_IDS || []).includes(empresa.id))
              )}
              sx={{ width: 'clamp(95px, 8vw, 120px)', flexShrink: 0, ...compactAutocompleteSx }}
            />

            {/* 3. Select Proyecto (Nivel 2) */}
            <Autocomplete
              disabled={loadingData || !(tempFilterData.EMPRESA_IDS || []).length}
              multiple
              disableCloseOnSelect
              forcePopupIcon
              size="small"
              options={proyectoOptions}
              value={proyectoOptions.filter(p => (tempFilterData.PROYECTOS || []).includes(p.id))}
              isOptionEqualToValue={(option, val) => option?.id === val?.id}
              onChange={(_, value) => {
                const updated = {
                  ...tempFilterData,
                  PROYECTOS: (value || []).map(p => p.id),
                  PROYECTO: (value || []).map(p => p.nombre).join(','),
                  DIVISIONES: [],
                  DIVISION: '',
                  CENTRO_COSTOS: [],
                  CENTRO_COSTO: ''
                };
                setTempFilterData(updated);
                setFilterData(updated);
                setTableParams(prev => ({ ...prev, page: 1 }));
              }}
              limitTags={1}
              renderTags={() => null}
              getOptionLabel={(option) => option?.nombre || ''}
              renderOption={renderCheckboxOption}
              renderInput={(params) => (
                renderSummaryInput(
                  params,
                  'Proyecto',
                  proyectoOptions.filter(p => (tempFilterData.PROYECTOS || []).includes(p.id)).map(p => p.nombre)
                )
              )}
              sx={{ width: 'clamp(90px, 7.5vw, 115px)', flexShrink: 0, ...compactAutocompleteSx }}
            />

            {/* 4. Select Grupo / División (Nivel 3) */}
            <Autocomplete
              disabled={loadingData || !(tempFilterData.EMPRESA_IDS || []).length}
              multiple
              disableCloseOnSelect
              forcePopupIcon
              size="small"
              options={divisionOptions}
              value={divisionOptions.filter(d => (tempFilterData.DIVISIONES || []).includes(d.id))}
              isOptionEqualToValue={(option, val) => option?.id === val?.id}
              onChange={(_, value) => {
                const updated = {
                  ...tempFilterData,
                  DIVISIONES: (value || []).map(d => d.id),
                  DIVISION: (value || []).map(d => d.nombre).join(','),
                  CENTRO_COSTOS: [],
                  CENTRO_COSTO: ''
                };
                setTempFilterData(updated);
                setFilterData(updated);
                setTableParams(prev => ({ ...prev, page: 1 }));
              }}
              limitTags={1}
              renderTags={() => null}
              getOptionLabel={(option) => option?.nombre || ''}
              renderOption={renderCheckboxOption}
              renderInput={(params) => (
                renderSummaryInput(
                  params,
                  'Grupo',
                  divisionOptions.filter(d => (tempFilterData.DIVISIONES || []).includes(d.id)).map(d => d.nombre)
                )
              )}
              sx={{ width: 'clamp(90px, 7.5vw, 112px)', flexShrink: 0, ...compactAutocompleteSx }}
            />

            {/* 5. Select Centro de Costo (Nivel 4) */}
            <Autocomplete
              disabled={loadingData || !(tempFilterData.EMPRESA_IDS || []).length}
              multiple
              disableCloseOnSelect
              forcePopupIcon
              size="small"
              options={centroCostoOptions}
              value={tempFilterData.CENTRO_COSTOS || []}
              onChange={(_, values) => updateVisibleFilter('CENTRO_COSTOS', values || [], { apply: false })}
              limitTags={1}
              renderTags={() => null}
              renderOption={renderCheckboxOption}
              renderInput={(params) => renderSummaryInput(params, 'Centro costo', tempFilterData.CENTRO_COSTOS || [])}
              sx={{ width: 'clamp(95px, 8.5vw, 120px)', flexShrink: 0, ...compactAutocompleteSx }}
            />

            {/* 6. Select Departamento */}
            <Autocomplete
              disabled={loadingData}
              multiple
              disableCloseOnSelect
              forcePopupIcon
              size="small"
              options={departamentoOptions}
              value={tempFilterData.DEPARTAMENTOS || []}
              onChange={(_, values) => updateVisibleFilter('DEPARTAMENTOS', values || [], { apply: false })}
              limitTags={1}
              renderTags={() => null}
              renderOption={renderCheckboxOption}
              renderInput={(params) => renderSummaryInput(params, 'Departamento', tempFilterData.DEPARTAMENTOS || [])}
              sx={{ width: 'clamp(95px, 8.5vw, 120px)', flexShrink: 0, ...compactAutocompleteSx }}
            />

            {/* 7. Select Proveedor */}
            <Autocomplete
              disabled={loadingData}
              multiple
              disableCloseOnSelect
              forcePopupIcon
              size="small"
              options={proveedorOptions}
              value={tempFilterData.PROVEEDORES || []}
              onChange={(_, values) => updateVisibleFilter('PROVEEDORES', values || [], { apply: false })}
              limitTags={1}
              renderTags={() => null}
              renderOption={renderCheckboxOption}
              renderInput={(params) => renderSummaryInput(params, 'Proveedor', tempFilterData.PROVEEDORES || [])}
              sx={{ width: 'clamp(98px, 9vw, 125px)', flexShrink: 0, ...compactAutocompleteSx }}
            />

            {/* 8. Select Divisa */}
            <Autocomplete
              disabled={loadingData}
              multiple
              disableCloseOnSelect
              forcePopupIcon
              size="small"
              options={divisaOptions}
              value={tempFilterData.DIVISAS || []}
              onChange={(_, values) => updateVisibleFilter('DIVISAS', values || [], { apply: false })}
              limitTags={1}
              renderTags={() => null}
              renderOption={renderCheckboxOption}
              renderInput={(params) => renderSummaryInput(params, 'Divisa', tempFilterData.DIVISAS || [])}
              sx={{ width: 'clamp(72px, 5.5vw, 85px)', flexShrink: 0, ...compactAutocompleteSx }}
            />

            {/* Separador vertical */}
            <Box sx={{ borderLeft: '1px solid #cbd5e1', height: 20, mx: 0.3, flexShrink: 0 }} />

            {/* 9. Buscador General al final */}
            <TextField
              disabled={loadingData}
              size="small"
              placeholder="Buscar..."
              value={quickSearch}
              onChange={(e) => setQuickSearch(e.target.value)}
              onKeyUp={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  applyFilters(tempFilterData, e.currentTarget.value);
                }
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon sx={{ color: '#0655A5', fontSize: 14 }} />
                  </InputAdornment>
                ),
                endAdornment: quickSearch ? (
                  <InputAdornment position="end">
                    <IconButton
                      disabled={loadingData}
                      size="small"
                      onClick={() => {
                        setQuickSearch('');
                        applyFilters(tempFilterData, '');
                      }}
                      sx={{ p: 0.1 }}
                    >
                      <ClearIcon sx={{ fontSize: 11 }} />
                    </IconButton>
                  </InputAdornment>
                ) : null,
              }}
              sx={{
                width: 'clamp(100px, 9vw, 130px)',
                flexShrink: 0,
                '& .MuiOutlinedInput-root': {
                  height: 28,
                  borderRadius: '10px',
                  bgcolor: '#ffffff',
                  fontSize: '0.68rem',
                  fontWeight: 500,
                  px: 0.8,
                  '& fieldset': { borderColor: '#cbd5e1' },
                  '&:hover fieldset': { borderColor: '#94a3b8' },
                  '&.Mui-focused fieldset': { borderColor: '#0655A5', borderWidth: '1.5px' },
                }
              }}
            />

            {/* 10. Botón Buscar */}
            <Button
              size="small"
              variant="contained"
              disabled={loadingData}
              onClick={() => applyFilters(tempFilterData, quickSearch)}
              sx={{
                height: 28,
                px: 1.2,
                fontSize: '0.70rem',
                fontWeight: 700,
                borderRadius: '10px',
                bgcolor: '#0655A5',
                color: '#ffffff',
                textTransform: 'none',
                boxShadow: 'none',
                flexShrink: 0,
                '&:hover': { bgcolor: '#054785' },
              }}
            >
              Buscar
            </Button>

            {/* 11. Botón Limpiar */}
            <Button
              size="small"
              variant="outlined"
              disabled={loadingData}
              onClick={() => {
                setTempFilterData(baseFilters);
                setFilterData(baseFilters);
                setQuickSearch('');
                setTableParams(prev => ({ ...prev, globalQuery: '', page: 1 }));
              }}
              startIcon={<ClearIcon sx={{ fontSize: 13 }} />}
              sx={{
                height: 28,
                px: 1,
                fontSize: '0.68rem',
                fontWeight: 600,
                borderRadius: '10px',
                borderColor: '#cbd5e1',
                color: '#64748b',
                textTransform: 'none',
                boxShadow: 'none',
                flexShrink: 0,
                '&:hover': { bgcolor: '#f8fafc', borderColor: '#94a3b8' },
              }}
            >
              Limpiar
            </Button>
          </Box>
        </Box>
      </Box>

      {/* ── Popover 1: Jerarquía Completa (Empresa > Proyecto > Grupo > CC) ── */}
      <Popover
        open={Boolean(hierarchicalAnchorEl)}
        anchorEl={hierarchicalAnchorEl}
        onClose={() => setHierarchicalAnchorEl(null)}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
        PaperProps={{
          elevation: 10,
          sx: {
            mt: 0.8,
            p: 2,
            borderRadius: '16px',
            border: '1px solid #e2e8f0',
            width: 320,
            maxWidth: '90vw',
            boxShadow: '0 20px 25px -5px rgba(15, 23, 42, 0.1), 0 8px 10px -6px rgba(15, 23, 42, 0.1)',
            display: 'flex',
            flexDirection: 'column',
            gap: 1.5,
          }
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', pb: 1, borderBottom: '1px solid #f1f5f9' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <AccountTreeIcon sx={{ color: '#0655A5', fontSize: 18 }} />
            <Typography sx={{ fontSize: 13, fontWeight: 800, color: '#0f172a' }}>
              Filtro Jerárquico
            </Typography>
          </Box>
          <IconButton size="small" onClick={() => setHierarchicalAnchorEl(null)}>
            <ClearIcon sx={{ fontSize: 14 }} />
          </IconButton>
        </Box>

        {/* 1. Empresa */}
        <Box>
          <Typography sx={{ fontSize: 11, fontWeight: 700, color: '#64748b', mb: 0.5 }}>
            1. EMPRESA
          </Typography>
          <Autocomplete
            disabled={loadingData}
            multiple
            disableCloseOnSelect
            forcePopupIcon
            size="small"
            options={empresaOptions}
            value={empresaOptions.filter(empresa => (tempFilterData.EMPRESA_IDS || []).includes(empresa.id))}
            onChange={(_, value) => {
              const updated = {
                ...tempFilterData,
                EMPRESA_IDS: (value || []).map(empresa => empresa.id),
                EMPRESA: (value || []).map(empresa => empresa.nombre).join(','),
                PROYECTOS: [],
                PROYECTO: '',
                DIVISIONES: [],
                DIVISION: '',
                CENTRO_COSTOS: [],
                CENTRO_COSTO: '',
              };
              setTempFilterData(updated);
            }}
            getOptionLabel={(option) => option?.nombre || ''}
            isOptionEqualToValue={(option, val) => option?.id === val?.id}
            renderOption={renderCheckboxOption}
            renderInput={(params) => renderSummaryInput(params, 'Empresa', empresaOptions.filter(empresa => (tempFilterData.EMPRESA_IDS || []).includes(empresa.id)).map(e => e.nombre))}
            fullWidth
          />
        </Box>

        {/* 2. Proyecto */}
        <Box>
          <Typography sx={{ fontSize: 11, fontWeight: 700, color: '#64748b', mb: 0.5 }}>
            2. PROYECTO
          </Typography>
          <Autocomplete
            disabled={loadingData || !(tempFilterData.EMPRESA_IDS || []).length}
            multiple
            disableCloseOnSelect
            forcePopupIcon
            size="small"
            options={proyectoOptions}
            value={proyectoOptions.filter(p => (tempFilterData.PROYECTOS || []).includes(p.id))}
            onChange={(_, value) => {
              const updated = {
                ...tempFilterData,
                PROYECTOS: (value || []).map(p => p.id),
                PROYECTO: (value || []).map(p => p.nombre).join(','),
                DIVISIONES: [],
                DIVISION: '',
                CENTRO_COSTOS: [],
                CENTRO_COSTO: '',
              };
              setTempFilterData(updated);
            }}
            getOptionLabel={(option) => option?.nombre || ''}
            isOptionEqualToValue={(option, val) => option?.id === val?.id}
            renderOption={renderCheckboxOption}
            renderInput={(params) => renderSummaryInput(params, 'Proyecto', proyectoOptions.filter(p => (tempFilterData.PROYECTOS || []).includes(p.id)).map(p => p.nombre))}
            fullWidth
          />
        </Box>

        {/* 3. Grupo / División */}
        <Box>
          <Typography sx={{ fontSize: 11, fontWeight: 700, color: '#64748b', mb: 0.5 }}>
            3. GRUPO / DIVISIÓN
          </Typography>
          <Autocomplete
            disabled={loadingData || !(tempFilterData.EMPRESA_IDS || []).length}
            multiple
            disableCloseOnSelect
            forcePopupIcon
            size="small"
            options={divisionOptions}
            value={divisionOptions.filter(d => (tempFilterData.DIVISIONES || []).includes(d.id))}
            onChange={(_, value) => {
              const updated = {
                ...tempFilterData,
                DIVISIONES: (value || []).map(d => d.id),
                DIVISION: (value || []).map(d => d.nombre).join(','),
                CENTRO_COSTOS: [],
                CENTRO_COSTO: '',
              };
              setTempFilterData(updated);
            }}
            getOptionLabel={(option) => option?.nombre || ''}
            isOptionEqualToValue={(option, val) => option?.id === val?.id}
            renderOption={renderCheckboxOption}
            renderInput={(params) => renderSummaryInput(params, 'Grupo / División', divisionOptions.filter(d => (tempFilterData.DIVISIONES || []).includes(d.id)).map(d => d.nombre))}
            fullWidth
          />
        </Box>

        {/* 4. Centro de Costo */}
        <Box>
          <Typography sx={{ fontSize: 11, fontWeight: 700, color: '#64748b', mb: 0.5 }}>
            4. CENTRO DE COSTO
          </Typography>
          <Autocomplete
            disabled={loadingData || !(tempFilterData.EMPRESA_IDS || []).length}
            multiple
            disableCloseOnSelect
            forcePopupIcon
            size="small"
            options={centroCostoOptions}
            value={tempFilterData.CENTRO_COSTOS || []}
            onChange={(_, value) => {
              updateVisibleFilter('CENTRO_COSTOS', value || [], { apply: false });
            }}
            renderOption={renderCheckboxOption}
            renderInput={(params) => renderSummaryInput(params, 'Centro costo', tempFilterData.CENTRO_COSTOS || [])}
            fullWidth
          />
        </Box>

        <Stack direction="row" spacing={1} justifyContent="flex-end" sx={{ mt: 0.5, pt: 1, borderTop: '1px solid #f1f5f9' }}>
          <Button
            size="small"
            variant="outlined"
            onClick={() => {
              const cleared = {
                ...tempFilterData,
                EMPRESA_IDS: [],
                EMPRESA: '',
                PROYECTOS: [],
                PROYECTO: '',
                DIVISIONES: [],
                DIVISION: '',
                CENTRO_COSTOS: [],
                CENTRO_COSTO: '',
              };
              setTempFilterData(cleared);
              setFilterData(cleared);
              setHierarchicalAnchorEl(null);
              setTableParams(prev => ({ ...prev, page: 1 }));
            }}
            sx={{ fontSize: 11, textTransform: 'none', borderRadius: '8px' }}
          >
            Limpiar Jerarquía
          </Button>
          <Button
            size="small"
            variant="contained"
            onClick={() => {
              applyFilters(tempFilterData, quickSearch);
              setHierarchicalAnchorEl(null);
            }}
            sx={{ fontSize: 11, textTransform: 'none', bgcolor: '#0655A5', borderRadius: '8px', '&:hover': { bgcolor: '#044384' } }}
          >
            Aplicar
          </Button>
        </Stack>
      </Popover>

      {/* ── Popover 2: Filtros Complementarios / Más Filtros ── */}
      <Popover
        open={showFiltersPanel}
        anchorEl={filterAnchorEl}
        onClose={() => setShowFiltersPanel(false)}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
        PaperProps={{
          elevation: 10,
          sx: {
            mt: 0.8,
            p: 2,
            borderRadius: '16px',
            border: '1px solid #e2e8f0',
            width: 320,
            maxWidth: '90vw',
            boxShadow: '0 20px 25px -5px rgba(15, 23, 42, 0.1), 0 8px 10px -6px rgba(15, 23, 42, 0.1)',
            display: 'flex',
            flexDirection: 'column',
            gap: 1.5,
          }
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', pb: 1, borderBottom: '1px solid #f1f5f9' }}>
          <Typography sx={{ fontSize: 13, fontWeight: 800, color: '#0f172a' }}>
            Filtros Complementarios
          </Typography>
          <IconButton size="small" onClick={() => setShowFiltersPanel(false)}>
            <ClearIcon sx={{ fontSize: 14 }} />
          </IconButton>
        </Box>

        <Box sx={{ display: 'flex', gap: 1 }}>
          <TextField
            size="small"
            type="date"
            label="Desde"
            value={tempFilterData.FECHA_INICIO}
            onChange={(e) => updateVisibleFilter('FECHA_INICIO', e.target.value, { apply: false })}
            InputLabelProps={{ shrink: true }}
            fullWidth
            sx={{ '& .MuiOutlinedInput-root': { height: '32px !important', borderRadius: '8px !important', fontSize: '0.78rem !important' } }}
          />
          <TextField
            size="small"
            type="date"
            label="Hasta"
            value={tempFilterData.FECHA_FIN}
            onChange={(e) => updateVisibleFilter('FECHA_FIN', e.target.value, { apply: false })}
            InputLabelProps={{ shrink: true }}
            fullWidth
            sx={{ '& .MuiOutlinedInput-root': { height: '32px !important', borderRadius: '8px !important', fontSize: '0.78rem !important' } }}
          />
        </Box>

        <Autocomplete
          disabled={loadingData}
          multiple
          disableCloseOnSelect
          forcePopupIcon
          size="small"
          options={departamentoOptions}
          value={tempFilterData.DEPARTAMENTOS || []}
          onChange={(_, value) => updateVisibleFilter('DEPARTAMENTOS', value || [], { apply: false })}
          renderOption={renderCheckboxOption}
          renderInput={(params) => renderSummaryInput(params, 'Departamento', tempFilterData.DEPARTAMENTOS || [])}
          fullWidth
        />

        <Autocomplete
          disabled={loadingData}
          multiple
          disableCloseOnSelect
          forcePopupIcon
          size="small"
          options={proveedorOptions}
          value={tempFilterData.PROVEEDORES || []}
          onChange={(_, value) => updateVisibleFilter('PROVEEDORES', value || [], { apply: false })}
          renderOption={renderCheckboxOption}
          renderInput={(params) => renderSummaryInput(params, 'Proveedor', tempFilterData.PROVEEDORES || [])}
          fullWidth
        />

        <Autocomplete
          disabled={loadingData}
          multiple
          disableCloseOnSelect
          forcePopupIcon
          size="small"
          options={divisaOptions}
          value={tempFilterData.DIVISAS || []}
          onChange={(_, value) => updateVisibleFilter('DIVISAS', value || [], { apply: false })}
          renderOption={renderCheckboxOption}
          renderInput={(params) => renderSummaryInput(params, 'Divisa', tempFilterData.DIVISAS || [])}
          fullWidth
        />

        <Stack direction="row" spacing={1} justifyContent="flex-end" sx={{ mt: 0.5, pt: 1, borderTop: '1px solid #f1f5f9' }}>
          <Button
            size="small"
            variant="outlined"
            onClick={() => {
              setTempFilterData(baseFilters);
              setFilterData(baseFilters);
              setQuickSearch('');
              setShowFiltersPanel(false);
            }}
            sx={{ fontSize: 11, textTransform: 'none', borderRadius: '8px' }}
          >
            Limpiar
          </Button>
          <Button
            size="small"
            variant="contained"
            onClick={() => {
              applyFilters(tempFilterData, quickSearch);
              setShowFiltersPanel(false);
            }}
            sx={{ fontSize: 11, textTransform: 'none', bgcolor: '#0655A5', borderRadius: '8px', '&:hover': { bgcolor: '#044384' } }}
          >
            Aplicar
          </Button>
        </Stack>
      </Popover>


      <Box sx={{ flexGrow: 1, overflow: 'hidden', minHeight: 0 }}>
        <CollapsibleTable
          dataTable={dataForTable}
          buttons={null}
          hiddenFields={tableHiddenFieldsMemo}
          columnLabels={tableColumnLabels}
          initialColumnOrder={compactColumnOrder}
          maxInlineActions={3}
          compactActions
          density="small"
          loading={loadingData}
          showExportButton={true}
          enableSorting={true}
          enableFiltering={false}
          leftButtons={
            <Box sx={{ display: { xs: 'none', xl: 'block' } }}>
              <TableFilterChips
                title="Estatus:"
                activeFilter={tempFilterData.ESTATUS || ''}
                onFilterClick={handleStatusFilterClick}
                items={statusChipsMemo}
                disabled={loadingData}
              />
            </Box>
          }
          persistEnabled={true}
          persistKey="SolicitudesAdmin"
          persistVista="layoutAdminV2"
          usuarioId={usuarioId}
          empresaId={empresaIdPersist}
          totalServerRows={data?.total || 0}
          serverPagination={true}
          onStateChange={handleTableStateChange}
          enableGlobalSearch={false}
          onRowClick={(row) => handleAdminStatusClick('solicitud', row)}
          onAdminStatusClick={handleAdminStatusClick}
          emptyStateData={emptyStateData}
          rightButtons={
            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
              <ExportButton
                data={dataForExport}
                onFetchAll={handleExportFetchAll}
                columnLabels={exportLabelsExtended}
                hiddenFields={exportHiddenFieldsMemo}
                fileName="Seguimiento_Solicitudes.xlsx"
                customExportOptions={customExportOptions}
                hidePendientes
                tableLoading={loadingData}
              />
            </Box>
          }
          mobileCard={{ statusMode: 'none' }}
          renderMobileFooter={renderMobileFooterMemo}
          renderSummary={() => null}
          renderFooterRow={({ orderedKeys, stickyLeftMeta, hasActions, selectable, hasPartidas }) => {
            if (!data?.totalesPorDivisa || Object.keys(data.totalesPorDivisa).length === 0) return null;

            let totalGeneralOC = 0;
            let totalGeneralSolicitado = 0;
            let totalGeneralRestante = 0;
            let countTotalGeneral = 0;

            Object.values(data.totalesPorDivisa).forEach(v => {
              totalGeneralOC += (v.total || 0);
              totalGeneralSolicitado += (v.solicitado || 0);
              totalGeneralRestante += (v.restante || 0);
              countTotalGeneral += (v.count || 0);
            });

            const displayTotalCount = data?.total || countTotalGeneral;

            const handleOpenPopover = (e, fieldKey) => {
              setPopoverAnchorEl(e.currentTarget);
              setPopoverActiveField(fieldKey);
            };

            const fmt = (n) => n.toLocaleString('es-MX', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

            const getBadgeStyle = (div) => {
              if (div === 'USD') return { color: '#b45309', bg: '#fef3c7' };
              if (div === 'EUR') return { color: '#4338ca', bg: '#e0e7ff' };
              if (div === 'MXN') return { color: '#0369a1', bg: '#e0f2fe' };
              if (div === 'GBP') return { color: '#701a75', bg: '#fae8ff' };
              if (div === 'CAD') return { color: '#991b1b', bg: '#fee2e2' };
              return { color: '#334155', bg: '#f1f5f9' };
            };

            const getCurrencySymbol = (div) => {
              if (div === 'EUR') return '€';
              if (div === 'GBP') return '£';
              return '$';
            };

            const divisasEntries = Object.entries(data.totalesPorDivisa || {}).sort((a, b) => {
              const order = { MXN: 1, USD: 2, EUR: 3 };
              return (order[a[0]] || 99) - (order[b[0]] || 99);
            });

            return (
              <React.Fragment>
                <tfoot style={{ position: 'sticky', bottom: 0, zIndex: 12, backgroundColor: '#f8fafc' }}>
                  <tr style={{ backgroundColor: '#f8fafc', borderTop: '2px solid #cbd5e1', borderBottom: '1px solid #cbd5e1' }}>
                    {selectable && <td style={{ backgroundColor: '#f8fafc', padding: '2px 4px' }} />}
                    {hasPartidas && <td style={{ backgroundColor: '#f8fafc', padding: '2px 4px' }} />}
                    
                    {orderedKeys.map((key) => {
                      const isSticky = stickyLeftMeta && stickyLeftMeta[key];
                      const stickyStyle = isSticky ? {
                        position: 'sticky',
                        left: isSticky.left,
                        zIndex: 13,
                        backgroundColor: '#f8fafc',
                        boxShadow: isSticky.isLastSticky ? '3px 0 6px -2px rgba(0, 0, 0, 0.12)' : 'none',
                      } : {};

                      const isFirstCol = key === orderedKeys[0];

                      let content = null;
                      let isClickable = false;
                      let fieldKey = null;

                      if (isFirstCol) {
                        content = (
                          <Typography sx={{ fontWeight: 900, fontSize: '0.65rem', color: '#334155', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                            TOTALES ({displayTotalCount})
                          </Typography>
                        );
                      } else if (key === 'TOTAL' || key === 'TOTAL_DIVISA') {
                        isClickable = true;
                        fieldKey = 'TOTAL';
                        content = (
                          <Box sx={{ display: 'flex', flexDirection: 'column', gap: '2px', py: 0.15 }}>
                            {divisasEntries.map(([divisa, vals]) => {
                              const total = vals.total || 0;
                              const sol = vals.solicitado || 0;
                              const rest = total > sol ? total - sol : (vals.restante || 0);
                              const { color: badgeColor, bg: badgeBg } = getBadgeStyle(divisa);
                              const sym = getCurrencySymbol(divisa);
                              return (
                                <Box key={divisa} sx={{ display: 'flex', flexDirection: 'column', lineHeight: 1.1 }}>
                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                    <Typography sx={{ fontSize: '0.55rem', fontWeight: 800, color: badgeColor, bgcolor: badgeBg, px: 0.4, py: 0.1, borderRadius: '3px', minWidth: 22, textAlign: 'center', lineHeight: 1.1 }}>
                                      {divisa}
                                    </Typography>
                                    <Typography sx={{ fontSize: '0.72rem', fontWeight: 850, color: total > 0 ? '#0f172a' : '#94a3b8', fontVariantNumeric: 'tabular-nums', lineHeight: 1.1, whiteSpace: 'nowrap' }}>
                                      {sym}{fmt(total)}
                                    </Typography>
                                  </Box>
                                  {rest > 0 && (
                                    <Typography sx={{ fontSize: '0.52rem', visibility: 'hidden', lineHeight: 1, mt: '1px' }}>
                                      -
                                    </Typography>
                                  )}
                                </Box>
                              );
                            })}
                          </Box>
                        );
                      } else if (key === 'MONTO_RESTANTE' || key === 'MONTOSOLICITADO' || key === 'SOLICITADO' || key === 'SOLICITADO_REST') {
                        isClickable = true;
                        fieldKey = 'SOLICITADO';
                        content = (
                          <Box sx={{ display: 'flex', flexDirection: 'column', gap: '2px', py: 0.15 }}>
                            {divisasEntries.map(([divisa, vals]) => {
                              const total = vals.total || 0;
                              const sol = vals.solicitado || 0;
                              const rest = total > sol ? total - sol : (vals.restante || 0);
                              const { color: badgeColor, bg: badgeBg } = getBadgeStyle(divisa);
                              const sym = getCurrencySymbol(divisa);
                              return (
                                <Box key={divisa} sx={{ display: 'flex', flexDirection: 'column', lineHeight: 1.1 }}>
                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                    <Typography sx={{ fontSize: '0.55rem', fontWeight: 800, color: badgeColor, bgcolor: badgeBg, px: 0.4, py: 0.1, borderRadius: '3px', minWidth: 22, textAlign: 'center', lineHeight: 1.1 }}>
                                      {divisa}
                                    </Typography>
                                    <Typography sx={{ fontSize: '0.72rem', fontWeight: 850, color: sol > 0 ? '#16a34a' : '#94a3b8', fontVariantNumeric: 'tabular-nums', lineHeight: 1.1, whiteSpace: 'nowrap' }}>
                                      {sym}{fmt(sol)}
                                    </Typography>
                                  </Box>
                                  {rest > 0 && (
                                    <Typography sx={{ fontSize: '0.55rem', fontWeight: 800, color: '#ea580c', fontVariantNumeric: 'tabular-nums', pl: '26px', mt: '1px', lineHeight: 1, whiteSpace: 'nowrap' }}>
                                      Rest: {sym}{fmt(rest)}
                                    </Typography>
                                  )}
                                </Box>
                              );
                            })}
                          </Box>
                        );
                      }

                      return (
                        <td
                          key={key}
                          onClick={(e) => isClickable && handleOpenPopover(e, fieldKey)}
                          style={{
                            ...stickyStyle,
                            padding: '3px 8px',
                            backgroundColor: isClickable ? '#f1f5f9' : '#f8fafc',
                            cursor: isClickable ? 'pointer' : 'default',
                            transition: 'background-color 0.15s',
                            verticalAlign: 'middle',
                          }}
                        >
                          {content}
                        </td>
                      );
                    })}

                    {hasActions && <td style={{ backgroundColor: '#f8fafc', padding: '3px 6px' }} />}
                  </tr>
                </tfoot>
              </React.Fragment>
            );
          }}
        />
      </Box>

      <StatusDetailDialog
        open={statusDetail.open}
        onClose={() => setStatusDetail(prev => ({ ...prev, open: false }))}
        type={statusDetail.type}
        row={statusDetail.row}
        loading={statusDetail.loading}
        data={statusDetail.data}
        apiUrl={apiUrl}
        solicitudesRelacionadas={solicitudesRelacionadas}
        loadingSolicitudesRelacionadas={loadingSolicitudesRelacionadas}
        onSelectSolicitud={(solicitud) => {
          if (!statusDetail.row) return;
          fetchSolicitudSummary(statusDetail.row, solicitud?.id);
        }}
        onOpenDetail={handleAdminStatusClick}
      />



      <CancelacionModal
        open={modalStates.openCancelacionModal}
        onClose={() => setModalStates((prev) => ({ ...prev, openCancelacionModal: false }))}
        onConfirm={cancelarSolicitud}
        solicitudData={modalStates}
      />

      <FileUploadModal
        open={fileUploadModalOpen}
        onClose={() => { setFileUploadModalOpen(false); setAdjuntos([]); }}
        onFilesSelected={setAdjuntos}
        onSave={handleUploadAdjuntos}
      />




      <SimpleSuccessSpinner
        open={loadingAction}
        message="Procesando..."
      />

      {/* ── POPOVER MINI DE DETALLE POR DIVISA AL DAR CLIC EN TOTALES ── */}
      <Popover
        open={Boolean(popoverAnchorEl)}
        anchorEl={popoverAnchorEl}
        onClose={() => {
          setPopoverAnchorEl(null);
          setPopoverActiveField(null);
        }}
        anchorOrigin={{
          vertical: 'top',
          horizontal: 'center',
        }}
        transformOrigin={{
          vertical: 'bottom',
          horizontal: 'center',
        }}
        PaperProps={{
          sx: {
            p: 1.5,
            borderRadius: '12px',
            boxShadow: '0 10px 25px -5px rgba(0,0,0,0.18), 0 8px 10px -6px rgba(0,0,0,0.1)',
            border: '1px solid #cbd5e1',
            minWidth: 260,
            bgcolor: '#ffffff',
          }
        }}
      >
        <Box sx={{ mb: 1, pb: 0.5, borderBottom: '1px solid #f1f5f9', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Typography sx={{ fontSize: 11, fontWeight: 900, color: '#0f172a', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
            {popoverActiveField === 'SOLICITADO' ? 'Detalle Solicitado por Divisa' : 'Detalle Total OC por Divisa'}
          </Typography>
          <Chip label="Detallado" size="small" sx={{ height: 16, fontSize: 8.5, fontWeight: 800, bgcolor: '#e0f2fe', color: '#0369a1' }} />
        </Box>

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.8 }}>
          {data?.totalesPorDivisa && Object.entries(data.totalesPorDivisa).map(([divisa, vals]) => {
            const total = vals.total || 0;
            const solicitado = vals.solicitado || 0;
            const restante = total > solicitado ? total - solicitado : (vals.restante || 0);
            const valToShow = popoverActiveField === 'SOLICITADO' ? solicitado : total;

            const colorMain =
              divisa === 'USD' ? '#b45309' :
              divisa === 'EUR' ? '#4338ca' :
              divisa === 'MXN' ? '#0369a1' :
              divisa === 'GBP' ? '#701a75' :
              divisa === 'CAD' ? '#991b1b' : '#374151';
            const bgBadge =
              divisa === 'USD' ? '#fef3c7' :
              divisa === 'EUR' ? '#e0e7ff' :
              divisa === 'MXN' ? '#e0f2fe' :
              divisa === 'GBP' ? '#fae8ff' :
              divisa === 'CAD' ? '#fee2e2' : '#f3f4f6';

            const getCurrencySymbol = (div) => {
              if (div === 'EUR') return '€';
              if (div === 'GBP') return '£';
              return '$';
            };
            const sym = getCurrencySymbol(divisa);

            const fmt = (n) => n.toLocaleString('es-MX', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

            return (
              <Box
                key={divisa}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  gap: 1.5,
                  p: 0.8,
                  borderRadius: '8px',
                  bgcolor: '#f8fafc',
                  border: '1px solid #f1f5f9',
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Chip
                    label={divisa}
                    size="small"
                    sx={{
                      height: 18,
                      fontSize: 9.5,
                      fontWeight: 900,
                      color: colorMain,
                      bgcolor: bgBadge,
                      '& .MuiChip-label': { px: 0.8 },
                    }}
                  />
                  <Typography sx={{ fontSize: 9.5, fontWeight: 700, color: '#64748b' }}>
                    ({vals.count || 0} OCs)
                  </Typography>
                </Box>

                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end' }}>
                  <Typography sx={{ fontSize: 12, fontWeight: 900, color: popoverActiveField === 'SOLICITADO' ? '#16a34a' : '#0f172a', fontVariantNumeric: 'tabular-nums' }}>
                    {sym}{fmt(valToShow)}
                  </Typography>
                  {popoverActiveField === 'SOLICITADO' && (
                    <Typography sx={{ fontSize: 9, fontWeight: 700, color: '#ea580c' }}>
                      Restante: {sym}{fmt(restante)}
                    </Typography>
                  )}
                </Box>
              </Box>
            );
          })}
        </Box>
      </Popover>
    </Box>
  );
}
